<script lang="ts">
  import TodayHero from '../lib/TodayHero.svelte';
  import TodayNav from '../lib/TodayNav.svelte';
  import { onDestroy, onMount, untrack } from 'svelte';
  import { localDay } from '../lib/format';
  import { isSupportView } from '../lib/supportView';
  import { currentView, stages, pollVersion, pollingActive, agentName, userEmail, georgeDiscussContext, chatSeed, replyDraftSeed, prospectingTileSeed, stageColor, stageLabel, terms, setClientStage } from '../lib/stores';
  import {
    getTodayBundle, getUpcomingEvents,
    markGeorgeMessageRead, markAllGeorgeRead, dismissGeorgeMessage, undismissGeorgeMessage,
    sendAwaitingReply, regenerateAwaitingReply, skipAwaitingReply,
    polishEmail,
    dismissDecision, undismissDecision, type HuntResponse,
    inviteGeorgeToEvent, removeGeorgeFromEvent, downloadCallBriefPdf,
    cancelScheduledEmail,
    setStage, setFollowup,
    getClientContacts, getInternalContacts,
    sendProactiveDraft, skipProactiveDraft, regenerateProactiveDraft,
    getThreadMessages, type ThreadMessage,
    type OutreachEntry,
    type InboundDraft, type Decision, type GeorgeMessage, type TodayBundle,
  } from '../lib/api';
  import { renderSafeMarkdown, unwrapHardBreaks, repairLostApostrophes } from '../lib/markdown';
  import { plainToHtml } from '../lib/email_markdown';
  import EmailBodyEditor from '../lib/EmailBodyEditor.svelte';
  import MultiSelect from '../lib/MultiSelect.svelte';
  import TrackRecordChart from '../lib/TrackRecordChart.svelte';
  import SyncButton from '../lib/SyncButton.svelte';
  import { createSectionSpy, jumpToSection } from '../lib/sectionSpy.svelte';

  // ─── state ───
  let georgeMessages = $state<GeorgeMessage[]>([]);
  // Read at the section header, which renders even when there are no briefs — so this
  // cannot live in an `{@const}` inside the list block.
  const briefUnread = $derived(georgeMessages.filter((m) => !m.read).length);
  let allDrafts = $state<InboundDraft[]>([]);
  let allDecisions = $state<Decision[]>([]);
  const isActiveClient = (client: string) =>
    client !== 'Non-Clients' && !['Non-Clients', 'On Hold'].includes($stages[client] || '');
  // Also gate cached bundles and already-rendered cards when stages change.
  const drafts = $derived(allDrafts.filter(d => isActiveClient(d.client)));
  const decisions = $derived(allDecisions.filter(d => isActiveClient(d.client)));
  let calls = $state<Array<{ id: string; summary: string; start: string; attendees: string[]; george_invited: boolean; has_meeting_url: boolean; has_external?: boolean; matched_client?: string }>>([]);
  let downloadingBrief = $state<string | null>(null);

  async function downloadBrief(eventId: string) {
    if (downloadingBrief) return;
    downloadingBrief = eventId;
    try {
      await downloadCallBriefPdf(eventId);
    } catch (e: any) {
      alert(`Couldn't get the brief: ${e?.message || e}`);
    }
    downloadingBrief = null;
  }
  let upcomingSends = $state<Array<{ id: string; send_at: string; to: string; subject: string; client: string; status: string; step: number; sequence_id: string }>>([]);
  let upcomingTotal = $state(0);  // total pending across all time, not just the displayed 5
  let hunt = $state<HuntResponse | null>(null);
  let progress = $state<{ goal: number; done: number; week_start: string } | null>(null);

  let loadingDrafts = $state(true);
  let loadingDecisions = $state(true);
  let editingDraftId = $state<string | null>(null);
  // Respond tiles default to collapsed (mirrors Briefing). Click the header
  // (or the body preview) to expand; clicking the body in expanded state
  // enters edit mode.
  let expandedDraft = $state<Record<string, boolean>>({});
  function toggleDraft(id: string) {
    expandedDraft[id] = !expandedDraft[id];
  }
  let editingDraftSubject = $state('');
  let editingDraftBody = $state('');
  // TO splits two ways (client contacts / other) — your own team would
  // never be the primary recipient on a reply, so the internal picker
  // is omitted. CC keeps the full three-way split (client / internal /
  // other) since looping a teammate in on a reply is common. On send
  // each set is merged + deduped into a single comma-joined string.
  let editingDraftToClient = $state<string[]>([]);
  let editingDraftToOther = $state('');
  let editingDraftCcClient = $state<string[]>([]);
  let editingDraftCcInternal = $state<string[]>([]);
  let editingDraftCcOther = $state('');
  // User-uploaded custom attachments for the currently-edited inbound
  // draft (Respond bucket). Mirrors editingProactiveCustomFiles above.
  let editingDraftCustomFiles = $state<File[]>([]);
  let editingClientContacts = $state<string[]>([]);
  let editingInternalContacts = $state<string[]>([]);
  let sendingDraftId = $state<string | null>(null);

  // Expand toggles — Calls / Re-engage / Critical all show a top-N preview.
  let callsExpanded = $state(false);
  let briefsExpanded = $state(false);
  let coldExpanded = $state(false);
  let criticalExpanded = $state(false);

  // Proactive drafts (overdue + going_cold) — own expand/edit/send state,
  // distinct from inbound drafts. Same shape because the tile is structurally
  // the same; separate so an inbound draft and a proactive draft for the same
  // client can each be open independently.
  let expandedProactive = $state<Record<string, boolean>>({});
  function toggleProactive(id: string) {
    expandedProactive[id] = !expandedProactive[id];
  }
  // Draft-less overdue (Chase) cards are made expandable too — keyed by
  // decisionKey since they have no proactive_draft id. They collapse to look
  // exactly like a with-draft chase card (so a missing draft doesn't advertise
  // itself) and expand to an "Open client" panel instead of a draft.
  let expandedNoDraft = $state<Record<string, boolean>>({});
  function toggleNoDraft(key: string) {
    expandedNoDraft[key] = !expandedNoDraft[key];
  }

  // "THEY WROTE" inbound preview — expand/collapse in place instead of an inner
  // scrollbar. Without it a long inbound scrolls inside an ~82px box; instead it clamps
  // to a few lines with a Show more/less toggle. Keyed by draft id.
  let expandedInbound = $state<Record<string, boolean>>({});
  function inboundIsLong(text: string): boolean {
    // No DOM measurement is available here, so approximate what the old ~4-line
    // (82px) box held: only show the toggle when the preview would overflow it —
    // a long single paragraph OR several hard line breaks.
    if (!text) return false;
    const newlines = (text.match(/\n/g) || []).length;
    return text.length > 240 || newlines >= 4;
  }

  // "View email thread" preview on overdue/going-cold cards — lazy-fetch the
  // last ~3 messages on first expand, cache per draft id so re-opening is free.
  let expandedThread = $state<Record<string, boolean>>({});
  let threadCache = $state<Record<string, ThreadMessage[] | 'loading'>>({});
  // A failure is NOT cached. Caching it meant the template's
  // `!Array.isArray(tc) || tc.length === 0` branch showed "Thread preview unavailable" —
  // the same words a genuinely empty thread gets — and, because the entry persisted,
  // re-opening never refetched, so one transient error made that thread unreadable for
  // the rest of the page session. Clearing the slot is what makes Retry work.
  let threadError = $state<Record<string, boolean>>({});
  async function loadThread(pdId: string, threadId: string) {
    threadCache[pdId] = 'loading';
    threadError[pdId] = false;
    try {
      const { messages } = await getThreadMessages(threadId, 10);
      threadCache[pdId] = messages;
    } catch {
      delete threadCache[pdId];
      threadCache = { ...threadCache };
      threadError[pdId] = true;
    }
  }
  async function toggleThread(pdId: string, threadId: string) {
    expandedThread[pdId] = !expandedThread[pdId];
    if (expandedThread[pdId] && threadCache[pdId] === undefined) {
      await loadThread(pdId, threadId);
    }
  }
  let editingProactiveId = $state<string | null>(null);
  let editingProactiveSubject = $state('');
  let editingProactiveBody = $state('');
  // TO/CC editing now uses the same three-way chip-picker pattern Respond
  // uses (client contacts + internal teammates + free-text "other") so the
  // editor reads the same across both tile families.
  let editingProactiveToClient = $state<string[]>([]);
  let editingProactiveToOther = $state('');
  let editingProactiveCcClient = $state<string[]>([]);
  let editingProactiveCcInternal = $state<string[]>([]);
  let editingProactiveCcOther = $state('');
  let editingProactiveClientContacts = $state<string[]>([]);
  let editingProactiveInternalContacts = $state<string[]>([]);
  // Prospecting tiles ship with AI-recommended attachments. Editor shows a
  // pill-style toggle row (same look as Prospecting page's chip picker) —
  // selected docs are filled with --accent, unselected are outlined.
  // `editingProactiveRecommended` snapshots the AI's original picks so we
  // can mark them with ✨ even after the user toggles them off.
  let editingProactiveAttachIds = $state<string[]>([]);
  let editingProactiveRecommended = $state<string[]>([]);
  // User-uploaded custom attachments for the currently-edited proactive
  // tile. Cleared when a different tile opens or when send completes.
  let editingProactiveCustomFiles = $state<File[]>([]);
  let marketingDocs = $state<OutreachEntry[]>([]);
  let sendingProactiveId = $state<string | null>(null);
  let regeneratingProactiveId = $state<string | null>(null);

  async function startEditProactive(d: Decision) {
    const pd = d.proactive_draft;
    if (!pd) return;
    expandedProactive[pd.id] = true;
    editingProactiveId = pd.id;
    editingProactiveSubject = pd.subject;
    editingProactiveBody = pd.body;
    // Seed attachments (prospecting drafts come with AI recommendations;
    // other types default to empty). Snapshot the recommendation list so
    // the ✨ marker survives the user toggling docs off / on.
    editingProactiveAttachIds = [...(pd.attach_ids || [])];
    editingProactiveRecommended = [...(pd.attach_ids || [])];
    editingProactiveCustomFiles = [];

    // Load contact pickers in parallel with the initial bucketing. If
    // contacts haven't resolved yet when we split, an address falls through
    // to "other"; when the lists resolve we re-bucket once.
    const [clientList, internalList] = await Promise.all([
      getClientContacts(d.client).catch(() => [] as string[]),
      getInternalContacts().catch(() => [] as string[]),
    ]);
    if (editingProactiveId !== pd.id) return;
    editingProactiveClientContacts = clientList;
    editingProactiveInternalContacts = internalList;

    const clientSet = new Set(clientList.map(e => e.toLowerCase()));
    const internalSet = new Set(internalList.map(e => e.toLowerCase()));
    const bucket = (addrs: string[]) => {
      const c: string[] = []; const i: string[] = []; const o: string[] = [];
      for (const addr of addrs) {
        const lc = addr.toLowerCase();
        if (clientSet.has(lc)) c.push(addr);
        else if (internalSet.has(lc)) i.push(addr);
        else o.push(addr);
      }
      return { c, i, o };
    };

    // Same TO-side rule Respond uses: a teammate on the TO line collapses
    // into "other" so the address still rides but the user sees it as plain
    // text rather than chip-picked from the internal list.
    const toAddrs = (pd.to || '').split(',').map(s => s.trim()).filter(Boolean);
    const toB = bucket(toAddrs);
    editingProactiveToClient = toB.c;
    editingProactiveToOther = [...toB.i, ...toB.o].join(', ');

    const ccAddrs = (pd.cc || '').split(',').map(s => s.trim()).filter(Boolean);
    const ccB = bucket(ccAddrs);
    editingProactiveCcClient = ccB.c;
    editingProactiveCcInternal = ccB.i;
    editingProactiveCcOther = ccB.o.join(', ');
  }
  function cancelEditProactive() {
    editingProactiveId = null;
    editingProactiveSubject = '';
    editingProactiveBody = '';
    editingProactiveToClient = [];
    editingProactiveToOther = '';
    editingProactiveCcClient = [];
    editingProactiveCcInternal = [];
    editingProactiveCcOther = '';
    editingProactiveClientContacts = [];
    editingProactiveInternalContacts = [];
    editingProactiveAttachIds = [];
    editingProactiveRecommended = [];
    editingProactiveCustomFiles = [];
  }
  function toggleProactiveAttach(docId: string) {
    if (editingProactiveAttachIds.includes(docId)) {
      editingProactiveAttachIds = editingProactiveAttachIds.filter(id => id !== docId);
    } else {
      editingProactiveAttachIds = [...editingProactiveAttachIds, docId];
    }
  }
  // Discuss for proactive drafts — same Strategize-chat seed pattern as
  // discussDraft uses for Respond. Routes to ClientView with $chatSeed (the
  // strategic-framing prompt) + $replyDraftSeed (the proposed draft pre-
  // loaded in the in-tab composer). Brief is type-flavored: overdue =
  // "we went quiet, here's the re-engagement, right play?"; going_cold =
  // "they've been cold N days, here's the cold-outreach proposal, is this
  // the right contact / angle?".
  function discussProactive(d: Decision) {
    const pd = d.proactive_draft;
    if (!pd) return;
    const cli = d.client.replace(/_/g, ' ');
    const proposed = `Subject: ${pd.subject}\n\n${pd.body}`;
    const isProspecting = d.type === 'prospecting';
    const isCold = d.type === 'going_cold';
    const isMeetingFollowup = d.type === 'meeting_followup';

    if (isMeetingFollowup) {
      const meetingDate = (pd as any).meeting_date || '';
      const dateClause = meetingDate ? ` on ${meetingDate}` : '';
      $chatSeed = {
        label: `Discuss post-meeting recap — ${cli}`,
        prompt: `We just had a meeting with ${cli}${dateClause}. The draft below is the post-meeting recap, addressed to the client-side attendees with our team on CC. Help me think through whether the recap captures the right things and whether anything strategic should be layered in.

--- The proposed recap ---
${proposed}

What I want from you:
- Did the recap capture what actually matters from the call? Anything missing — a commitment we made, a concern they raised, a next step that needs naming?
- Is the tone right — too pushy, too soft, too generic? Are we being specific enough about what we discussed?
- Anything strategic worth adding (a specific resource to send, an intro to make, a calendar block to propose) that the recap doesn't already do?
- Is the recipient list right (TO: ${pd.to || '(none)'} ; CC: ${pd.cc || '(none)'})? Anyone who was on the call we shouldn't be writing to, or anyone we missed?

Be direct. The composer in the Emails tab has the recap pre-loaded — I'll port your edits or write fresh after we think it through.`,
      };
      // Fresh email — empty thread_id. CC includes both client attendees and own-team.
      $replyDraftSeed = {
        client: d.client,
        threadId: '',
        to: pd.to,
        ccClient: [], ccInternal: [],
        ccOther: pd.cc || '',
        subject: pd.subject,
        body: pd.body,
        brief: (pd as any).brief || '',
      };
      $currentView = d.client;
      return;
    }

    if (isProspecting) {
      // Prospecting Discuss routes to the Prospecting tab (its native
      // surface), NOT ClientView. ClientView is for engaged-relationship
      // management; a cold prospect isn't an engaged client yet. The
      // Prospecting tab already has the right composer (Pipeline B /
      // generate_outreach) + conversion examples + persona pickers.
      $chatSeed = {
        label: `Discuss prospecting — ${cli}`,
        prompt: `Help me think through how to best get ${cli} to engage with us.

Proposed cold email — to ${pd.to || '(no recipient)'}:
${proposed}

What I want from you:
- Is this the right contact at ${cli}, or is there a stronger entry point?
- Is the angle / opening right, or is there a sharper hook (a specific project, a recent event, a piece of news) that would land harder?
- Should I be sending a cold reach at all to this account, or is there a warmer route (a referral, a different channel, a wait-and-watch)?

Be direct. The Generate tab has the proposed draft pre-loaded — I'll port your edits or write fresh after we think it through.`,
      };
      $prospectingTileSeed = {
        client: d.client,
        contactEmail: pd.to || '',
        contactName: (pd as any).contact_name || '',
        subject: pd.subject || '',
        body: pd.body || '',
        brief: (pd as any).brief || '',
      };
      $currentView = 'prospecting';
      return;
    }

    // For overdue: LIGHT vs HEAVY come from the backend's
    // unanswered_outbound_count on the proactive_draft payload. The same
    // split the _generate_for_row router uses ⇒ count==1 means one
    // unanswered outbound (LIGHT — soft nudge); count>=2 (or 0) means
    // multiple unanswered (HEAVY — full re-engagement). The Discuss prompt
    // mirrors the framing so chat picks up on what the draft was written
    // under, not just the time gap.
    const unansweredCount = (pd as any).unanswered_outbound_count || 0;
    const isLightOverdue = !isCold && unansweredCount === 1;
    const flavor = isCold
      ? {
          label: `Discuss cold reach-out — ${cli}`,
          prompt: `${cli} has gone quiet — ${d.age_days} days since the last follow-up. The draft below would be a cold reach-out (a NEW conversation, not a reply). Help me think through whether this is the strongest move and if not, what is.

--- The proposed cold reach-out ---
${proposed}

What I want from you:
- Is this the right person to address, or would another contact at ${cli} be a stronger entry point?
- Is the angle here the right one, or is there a sharper hook (a specific project, a recent event, a piece of news) that would land harder?
- Should I be sending a cold reach-out at all, or is there a warmer route (a referral, a different channel, sitting on it longer)?

Be direct. If you'd take a different approach entirely, say so plainly. The composer in the Emails tab has the proposed draft pre-loaded — I'll port your edits or write fresh after we think it through.`,
        }
      : isLightOverdue
      ? {
          label: `Discuss nudge — ${cli}`,
          prompt: `${cli} hasn't replied to your last email yet — it's been ${d.age_days} days. The draft below is a soft nudge that adds one new piece of useful context rather than re-pitching the prior email. Help me think through whether nudging is the right move and if so what the strongest version looks like.

--- The proposed nudge ---
${proposed}

What I want from you:
- Is a nudge the right call at this point, or should I sit on it longer / try a different person at ${cli} / pull a different lever?
- If a nudge IS right, is the angle (the new context being added) the strongest available? What would land harder?
- Is there anything specific I should layer in (a concrete next step, a small commitment we can offer, an introduction) that the draft is missing?

Be direct. If you'd do something different, say so plainly. The composer in the Emails tab has the proposed nudge pre-loaded — I'll port your edits or write fresh after we think it through.`,
        }
      : {
          label: `Discuss re-engagement — ${cli}`,
          prompt: `${cli} is overdue on follow-up — the thread went quiet ${d.age_days} days ago${unansweredCount > 1 ? `, with ${unansweredCount} outbound messages from us in a row and no reply` : ''}. The draft below would continue that thread with a re-engagement note. Help me think through whether continuing here is the strongest move and if not, what is.

--- The proposed re-engagement reply ---
${proposed}

What I want from you:
- What's actually happening with this account right now? What's the trajectory and where's the friction?
- Is continuing this thread the right move, or is there a stronger play — a different contact, a new angle, sitting on it longer, escalating, or letting it die?
- If continuing is roughly right, anything strategic to layer in (a specific offer, an introduction, a piece of insight) that would do more work than just "checking in"?

Be direct. If you'd do something different, say so plainly. The composer in the Emails tab has the proposed draft pre-loaded — I'll port your edits or write fresh after we think it through.`,
        };
    $chatSeed = flavor;
    $replyDraftSeed = {
      client: d.client,
      threadId: pd.thread_id || '',
      to: pd.to,
      ccClient: [],
      ccInternal: [],
      ccOther: pd.cc || '',
      subject: pd.subject,
      body: pd.body,
      brief: (pd as any).brief || '',
    };
    $currentView = d.client;
  }

  // Mirrors polishOrRefine — same API, just bound to the proactive editor.
  // Polish = small wording fixes, Refine = larger restructuring. Result
  // replaces the in-editor body/subject and shows a "what changed" note.
  async function polishOrRefineProactive(d: Decision, mode: 'polish' | 'refine') {
    const pd = d.proactive_draft;
    if (!pd || editingProactiveId !== pd.id) return;
    regeneratingProactiveId = pd.id;   // reuse the "spinner" lock
    polishChanges = '';
    try {
      const r = await polishEmail({
        client: d.client, body: editingProactiveBody,
        subject: editingProactiveSubject, mode,
      });
      editingProactiveBody = r.body;
      if (!d.proactive_draft?.thread_id) editingProactiveSubject = r.subject;
      polishChanges = r.changes || '';
    } catch (e: any) {
      polishChanges = `${mode} failed: ${e?.message || e}`;
    }
    regeneratingProactiveId = null;
  }

  async function sendProactive(d: Decision) {
    const pd = d.proactive_draft;
    if (!pd) return;
    sendingProactiveId = pd.id;
    try {
      const editing = editingProactiveId === pd.id;
      const to = editing
        ? mergeTriple(editingProactiveToClient, [], editingProactiveToOther)
        : undefined;
      const cc = editing
        ? mergeTriple(editingProactiveCcClient, editingProactiveCcInternal, editingProactiveCcOther)
        : undefined;
      await sendProactiveDraft(
        pd.id,
        editing ? editingProactiveSubject : undefined,
        editing ? editingProactiveBody : undefined,
        to,
        cc,
        editing ? editingProactiveAttachIds : undefined,
        editing && editingProactiveCustomFiles.length
          ? editingProactiveCustomFiles : undefined,
      );
      sentProactiveIds.add(pd.id);
      invalidateTodayCache('decisions');
      // Optimistic: drop the proactive_draft from the in-memory row so the
      // tile collapses to the standard action-only view (or disappears if it
      // was the only thing showing).
      const idx = allDecisions.findIndex(x => x.client === d.client && x.type === d.type);
      if (idx >= 0) {
        const { proactive_draft, ...rest } = allDecisions[idx];
        allDecisions[idx] = rest as Decision;
      }
      editingProactiveId = null;
    } catch (e: any) {
      alert(`Send failed: ${e?.message || e}`);
    }
    sendingProactiveId = null;
  }
  // Dismiss an inbound draft (Respond bucket) — mirrors the swipe-right
  // commit branch but invoked from the button. Marks the row 'skipped'
  // server-side and parks it in dismissedTiles so the back-tile + Undo
  // affordance renders (same UX every other dismissible tile in Today
  // has — Critical, Re-engage, Briefing, meeting_followup).
  async function skipDraft(d: InboundDraft) {
    try {
      await skipAwaitingReply(d.id);
      invalidateTodayCache('drafts');
      // Keep the row in `drafts` and add to dismissedTiles — same
      // pattern decisionCard / proactiveDraftCard / georgeCard use.
      // The inline render branches on dismissedTiles.has(d.id) to show
      // the Undo back-tile in place of the full draft card.
      dismissedTiles.set(d.id, { kind: 'draft', id: d.id, draft: d });
      dismissedTiles = new Map(dismissedTiles);
    } catch (e: any) {
      alert(`Dismiss failed: ${e?.message || e}`);
    }
  }

  async function skipProactive(d: Decision) {
    const pd = d.proactive_draft;
    if (!pd) return;
    // meeting_followup: the decision IS its pending proactive_draft (today.py
    // synthesizes the card from the pending row), so hiding it means skipping
    // the draft. But a bare skip does NOT stick — the backend self-heal deletes
    // the 'skipped' row and _find_meeting_followups re-emits it next sync. So we
    // ALSO stamp a decision_dismissal: that makes the self-heal spare the row
    // and gates re-enqueue (proactive_draft_svc meeting_followup pass). No Undo
    // (no un-skip endpoint); the dismissal is state-based so it re-surfaces if
    // the thread advances.
    if (d.type === 'meeting_followup') {
      try {
        await skipProactiveDraft(pd.id);
        await dismissDecision(d.client, d.type, 7, /* state_based */ true);
        invalidateTodayCache('decisions');
        allDecisions = allDecisions.filter(x => !(x.client === d.client && x.type === d.type));
      } catch (e: any) {
        alert(`Dismiss failed: ${e?.message || e}`);
      }
      return;
    }
    // prospecting: skip sticks on its own — the self-heal excludes prospecting,
    // so a 'skipped' row stays skipped and the top-up picks a different account.
    if (d.type === 'prospecting') {
      try {
        await skipProactiveDraft(pd.id);
        invalidateTodayCache('decisions');
        allDecisions = allDecisions.filter(x => !(x.client === d.client && x.type === d.type));
      } catch (e: any) {
        alert(`Skip failed: ${e?.message || e}`);
      }
      return;
    }
    // overdue (and other decision-condition types that carry a draft): suppress
    // the DECISION (reversible via the back-tile Undo → undismissDecision) and
    // leave the draft intact so Undo restores the full card.
    try {
      await dismissDecision(d.client, d.type, 7, /* state_based */ true);
      invalidateTodayCache('decisions');
      dismissedTiles.set(decisionKey(d), { kind: 'decision', client: d.client, type: d.type });
      dismissedTiles = new Map(dismissedTiles);
    } catch (e: any) {
      alert(`Dismiss failed: ${e?.message || e}`);
    }
  }
  async function regenerateProactive(d: Decision) {
    const pd = d.proactive_draft;
    if (!pd) return;
    regeneratingProactiveId = pd.id;
    try {
      const r = await regenerateProactiveDraft(pd.id);
      invalidateTodayCache('decisions');
      const idx = allDecisions.findIndex(x => x.client === d.client && x.type === d.type);
      if (idx >= 0 && allDecisions[idx].proactive_draft) {
        allDecisions[idx] = {
          ...allDecisions[idx],
          proactive_draft: {
            ...(allDecisions[idx].proactive_draft as any),
            subject: pd.thread_id ? pd.subject : r.subject, body: r.body, to: r.to, cc: r.cc,
            // attach_ids comes back from the regen for prospecting drafts
            // (generate_outreach's recommended_attachments). Mirror it
            // onto the tile so subsequent edits + sends see the new
            // attachment set, not the stale one from before regen.
            attach_ids: (r as any).attach_ids || [],
          },
        };
      }
      if (editingProactiveId === pd.id) {
        if (!pd.thread_id) editingProactiveSubject = r.subject;
        editingProactiveBody = r.body;
        // If the editor is open, sync the editable attachment chips too.
        const newAttachIds: string[] = (r as any).attach_ids || [];
        editingProactiveAttachIds = [...newAttachIds];
        editingProactiveRecommended = [...newAttachIds];
        // TO/CC live in the chip-picker triple (client chips +
        // internal chips + free-text "other"). Mirror the same
        // bucketing startEditProactive does so the editor's
        // chips stay consistent with the regenerated addresses.
        const clientSet = new Set(editingProactiveClientContacts.map(e => e.toLowerCase()));
        const internalSet = new Set(editingProactiveInternalContacts.map(e => e.toLowerCase()));
        const bucket = (addrs: string[]) => {
          const c: string[] = []; const i: string[] = []; const o: string[] = [];
          for (const addr of addrs) {
            const lc = addr.toLowerCase();
            if (clientSet.has(lc)) c.push(addr);
            else if (internalSet.has(lc)) i.push(addr);
            else o.push(addr);
          }
          return { c, i, o };
        };
        const toAddrs = (r.to || '').split(',').map((s: string) => s.trim()).filter(Boolean);
        const toB = bucket(toAddrs);
        editingProactiveToClient = toB.c;
        editingProactiveToOther = [...toB.i, ...toB.o].join(', ');
        const ccAddrs = (r.cc || '').split(',').map((s: string) => s.trim()).filter(Boolean);
        const ccB = bucket(ccAddrs);
        editingProactiveCcClient = ccB.c;
        editingProactiveCcInternal = ccB.i;
        editingProactiveCcOther = ccB.o.join(', ');
      }
    } catch (e: any) {
      alert(`Regenerate failed: ${e?.message || e}`);
    }
    regeneratingProactiveId = null;
  }
  // From George — a collapsed numbered list; expand a note to read it.
  let expandedGeorge = $state<Record<string, boolean>>({});

  // Derive a first name from email for the greeting
  const firstName = $derived(($userEmail || '').split('@')[0].split('.')[0].replace(/^\w/, c => c.toUpperCase()));

  // ─── decision → bucket partition ───
  // The single /today/decisions feed is split client-side by `type`.
  // Re-engage = going_cold; Prospect = behind_target/prospecting;
  // everything else (incl. unknown future types) falls through to Critical.
  const REENGAGE_TYPES = new Set(['going_cold']);
  const PROSPECT_DECISION_TYPES = new Set(['behind_target', 'prospecting']);
  // meeting_followup renders under Respond — the rep just had the meeting
  // and needs to ship a recap. Carrying a draft already attached, same
  // proactive-card markup as overdue/going_cold.
  const RESPOND_DECISION_TYPES = new Set(['meeting_followup']);
  // Temporarily hidden: score_drop / "Relationship weakening" until its
  // threshold is revisited.
  const SUPPRESSED_TYPES = new Set(['score_drop']);
  const criticalDecisions = $derived(
    decisions.filter(d => !REENGAGE_TYPES.has(d.type)
                       && !PROSPECT_DECISION_TYPES.has(d.type)
                       && !RESPOND_DECISION_TYPES.has(d.type)
                       && !SUPPRESSED_TYPES.has(d.type))
  );
  // Overdue cards with no proactive_draft are SHOWN, not hidden — as an
  // expandable card that mirrors a with-draft chase card from the outside and
  // opens to an "Open client →" panel (see the criticalCard snippet). The draft
  // is the nicety; "you're overdue, go act on this client" still has real
  // standalone value, so we give a path instead of vanishing the card. We
  // track them only so the [today-diag] effect can flag a draft that was
  // EXPECTED but never landed (queued / skipped-by-design / gen-failed — the
  // frontend can't tell which; that "why" lives in the backend).
  const criticalNoDraft = $derived(
    criticalDecisions.filter(d => d.type === 'overdue' && !d.proactive_draft)
  );
  const meetingFollowupDecisions = $derived(
    decisions.filter(d => RESPOND_DECISION_TYPES.has(d.type))
  );
  // The subset that actually RENDERS — a draft-less meeting_followup is hidden
  // from the user by design (see the [today-diag] effect below). The section
  // numbers cards off this list, not the full one: numbering off the full list
  // meant a hidden card consumed an ordinal and the visible list read "2, 3, 4".
  const respondFollowups = $derived(
    meetingFollowupDecisions.filter(d => !!d.proactive_draft)
  );
  // Combined Respond count = inbound drafts + meeting follow-ups. Used by
  // the pill bar AND the Respond section header — they must agree.
  const respondCount = $derived(drafts.length + meetingFollowupDecisions.length);
  // Ranked least-cold → most-cold (ascending days silent). The freshly-cooled
  // clients lead the top-3 preview because they're the most salvageable.
  const reengageDecisions = $derived(
    decisions.filter(d => REENGAGE_TYPES.has(d.type))
             .sort((a, b) => (a.age_days || 0) - (b.age_days || 0))
  );
  const prospectDecisions = $derived(decisions.filter(d => PROSPECT_DECISION_TYPES.has(d.type)));
  // behind_target restates the progress bar — drop the card when the bar shows.
  const prospectCards = $derived(
    prospectDecisions.filter(d => d.type !== 'prospecting'
                               && !(d.type === 'behind_target' && !!progress && progress.goal > 0))
  );
  // Prospecting drafts are their own family — Respond-styled tiles, white
  // accent, attachment editor. They get their own render block above the
  // hunt grid so the user sees the AI-prepared cold outreach next to the
  // hunt suggestions.
  // Gated on the draft, not just the type: `sendProactive` optimistically strips
  // `proactive_draft` off the row, and `proactiveDraftCard` dereferences `pd.id`
  // — an ungated row threw during render on the very next tick after a send.
  // Every other bucket gates at the call site (`{#if d.proactive_draft}`); New
  // business renders unconditionally, so the gate lives here instead.
  const prospectingDrafts = $derived(
    prospectDecisions.filter(d => d.type === 'prospecting' && !!d.proactive_draft)
  );
  const visibleCalls = $derived(callsExpanded ? calls : calls.slice(0, 3));
  const visibleCold = $derived(coldExpanded ? reengageDecisions : reengageDecisions.slice(0, 3));
  // Top-5 + show-all. 5 was chosen as a visible-without-overwhelming default;
  // the section header still shows the true total count.
  const visibleCritical = $derived(criticalExpanded ? criticalDecisions : criticalDecisions.slice(0, 5));

  // ── Dev diagnostics (console only) ──────────────────────────────────
  // Two silent states hide bugs from the DEV: (1) a Respond (meeting_followup)
  // card with no draft VANISHES entirely (see the {#if d.proactive_draft}
  // gate below) — a draft that should have generated but didn't; (2) a Today
  // that looks "all clear" but is actually all-suppressed. Chase overdue-no-
  // draft is NO LONGER hidden (it shows a slim Open-client card), but we still
  // note it — a draft was expected there too. This effect narrates all three
  // to the browser console — no persistence, no UI, no change to what the user
  // sees. Filter the console by "[today-diag]". A non-reactive signature guard
  // keeps it from re-logging the same picture on every reactive tick.
  let _diagSig = '';
  $effect(() => {
    if (loadingDrafts || loadingDecisions) return;  // don't cry about a mid-load blank
    // Respond (meeting_followup) no-draft cards are HIDDEN; Chase overdue
    // no-draft cards are SHOWN (slim Open-client card) but still worth a note.
    const hiddenRespond = meetingFollowupDecisions.filter(d => !d.proactive_draft);
    const draftlessChase = criticalNoDraft;
    const visibleWork =
      drafts.length + meetingFollowupDecisions.filter(d => !!d.proactive_draft).length +
      criticalDecisions.length + reengageDecisions.length +
      prospectCards.length + prospectingDrafts.length + calls.length;
    // "Empty Today": nothing visible. Suspicious only if decisions DID exist and
    // all got filtered/hidden — that's the "went to a client, came back, saw
    // nothing" case, distinct from an earned inbox-zero.
    const emptySuspicious = visibleWork === 0 && decisions.length > 0;

    const sig = JSON.stringify({
      hr: hiddenRespond.map(d => `${d.client}|${d.type}`),
      dc: draftlessChase.map(d => `${d.client}|${d.type}`),
      es: emptySuspicious ? decisions.length : 0,
    });
    if (sig === _diagSig) return;   // same picture — stay quiet
    _diagSig = sig;

    for (const d of hiddenRespond) {
      console.warn(`[today-diag] card.suppressed reason=no_draft section=Respond client=${d.client} type=${d.type} — card hidden from user`);
    }
    for (const d of draftlessChase) {
      console.info(`[today-diag] draft.missing reason=no_draft section=Chase client=${d.client} type=${d.type} — shown as Open-client card (no draft to send)`);
    }
    if (emptySuspicious) {
      console.warn(`[today-diag] today.empty reason=all_suppressed — ${decisions.length} decision(s) existed but none are visible (NOT inbox-zero)`);
    }
  });

  // George's one-line status, spoken in his voice.
  const georgeStatus = $derived.by(() => {
    // Hold the neutral loading line until the first data fetch resolves
    // so the empty-state ("nothing on fire") doesn't flash for one tick
    // before the real counts arrive.
    if (!initialLoadDone) return "Pulling together your day…";
    if (sectionUnavailable('decisions') || sectionUnavailable('drafts') || calendarUnavailable)
      return "I'm still checking part of your day. Retry to get the full picture.";
    const c = criticalDecisions.length;
    const r = drafts.length + meetingFollowupDecisions.length;
    const cl = calls.length;
    const bits: string[] = [];
    if (c) bits.push(`${c} thing${c === 1 ? ' needs' : 's need'} a call from you`);
    if (r) bits.push(`${r} repl${r === 1 ? 'y is' : 'ies are'} drafted and ready`);
    if (cl) bits.push(`${cl} call${cl === 1 ? '' : 's'} on today's calendar`);
    if (!bits.length) return "I've been through every account — nothing's on fire and nothing's slipping. Clean slate. Good day to go hunting.";
    const joined = bits.length === 1 ? bits[0] : bits.slice(0, -1).join(', ') + ' and ' + bits[bits.length - 1];
    return `I've been through your accounts — ${joined}. Let's clear the top of the page, then go build pipeline.`;
  });
  const transitionLine = $derived(
    (sectionUnavailable('decisions') || sectionUnavailable('drafts'))
      ? "Some reactive work is temporarily unavailable."
      : (criticalDecisions.length === 0 && drafts.length === 0 && meetingFollowupDecisions.length === 0)
      ? "Nothing reactive today — straight to building the pipeline."
      : "Above is what we've built. Below is what we'll build next."
  );

  // True after the first /today fetch or a cached bundle is applied.
  // Keep empty states visible during later background refreshes: loadAll()
  // sets loadingDrafts/loadingDecisions on every poll, which otherwise makes
  // empty sections disappear and reappear every few seconds.
  let initialLoadDone = $state(false);

  // ─── data loading — ONE round-trip via /today/bundle ───
  // Replaces 9 separate XHRs. Backend runs the sub-fetches in parallel
  // via asyncio.gather so latency = max sub-query time, not sum.
  // A null sub-result means that section failed; preserve its last valid data.
  const INFORMATIONAL_GEORGE = new Set(['post_call', 'score_surge', 'score_drop', 'dry_funnel', 'welcome', 'build_complete', 'overdue']);

  // ── Stale-while-revalidate cache ──────────────────────────────────────
  // Persist the parsed Today bundle (+ calls) so a return visit paints the
  // last-known state instantly, then revalidates in the background. The user
  // never stares at a blank/loading Today while the network round-trips.
  const TODAY_CACHE_KEY = `today_bundle_v2_${$userEmail}`;
  const TODAY_CALLS_KEY = `today_calls_v2_${$userEmail}`;
  const viewCache = isSupportView() ? null : localStorage;
  let latestBundle: TodayBundle | null = null;
  let bundleSeq = 0;
  let calendarSeq = 0;
  let destroyed = false;
  let refreshQueued = false;
  let unavailableSections = $state<string[]>([]);
  let calendarUnavailable = $state(false);
  const sentDraftIds = new Set<string>();
  const sentProactiveIds = new Set<string>();
  const VISIBLE_BUNDLE_SECTIONS: (keyof TodayBundle)[] = [
    'decisions', 'drafts', 'scheduled', 'george_messages', 'hunt', 'progress', 'outreach',
  ];
  function sectionUnavailable(section: string): boolean {
    return unavailableSections.includes('all') || unavailableSections.includes(section);
  }
  onDestroy(() => { destroyed = true; ++bundleSeq; ++calendarSeq; });

  // Discard snapshots started before a successful write. The affected section
  // must be fetched again before it can enter the persistent cache.
  function invalidateTodayCache(...sections: (keyof TodayBundle)[]) {
    ++bundleSeq;
    if (latestBundle) {
      for (const section of sections) (latestBundle as any)[section] = null;
    }
    try { viewCache?.removeItem(TODAY_CACHE_KEY); } catch {}
    if (!refreshQueued && !destroyed) {
      refreshQueued = true;
      queueMicrotask(() => {
        refreshQueued = false;
        if (!destroyed) void loadAll();
      });
    }
  }

  function withoutSentCards(b: TodayBundle): TodayBundle {
    return {
      ...b,
      decisions: b.decisions && {
        ...b.decisions,
        decisions: b.decisions.decisions.map((d: Decision) => {
          if (!d.proactive_draft || !sentProactiveIds.has(d.proactive_draft.id)) return d;
          const { proactive_draft, ...rest } = d;
          return rest;
        }),
      },
      drafts: b.drafts && {
        ...b.drafts,
        drafts: b.drafts.drafts.filter((d: InboundDraft) => !sentDraftIds.has(d.id)),
      },
    };
  }

  // Apply a raw /today/bundle payload to component state. Pure parsing — no
  // network — so the same code path serves both the cached and fresh payloads.
  function applyBundle(b: TodayBundle) {
    if (b.decisions != null) {
      const seen = new Set<string>();
      const next = b.decisions.decisions.filter((d: Decision) => {
        const k = `${d.client}|${d.type}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      // A dismissed card keeps its Undo back tile until the user leaves or
      // explicitly undoes it, even if the post-write refresh omits that row.
      for (const entry of dismissedTiles.values()) {
        if (entry.kind !== 'decision' && entry.kind !== 'put-on-hold') continue;
        const old = allDecisions.find(d => d.client === entry.client && d.type === entry.type);
        if (old && !next.some(d => d.client === old.client && d.type === old.type)) next.push(old);
      }
      allDecisions = next;
    }
    if (b.drafts != null) {
      const next = b.drafts.drafts as InboundDraft[];
      for (const entry of dismissedTiles.values()) {
        if (entry.kind === 'draft' && !next.some(d => d.id === entry.id)) next.push(entry.draft);
      }
      allDrafts = next;
    }
    if (b.george_messages != null) {
      const cutoffG = Date.now() - 7 * 86400000;
      const next = b.george_messages.filter(m => {
        if (!INFORMATIONAL_GEORGE.has(m.msg_type)) return false;
        const ts = m.created_at.endsWith('Z') ? m.created_at : m.created_at + 'Z';
        return new Date(ts).getTime() >= cutoffG;
      });
      for (const entry of dismissedTiles.values()) {
        if (entry.kind !== 'brief') continue;
        const old = georgeMessages.find(m => m.id === entry.id);
        if (old && !next.some(m => m.id === old.id)) next.push(old);
      }
      georgeMessages = next;
    }
    if (b.scheduled != null) {
      const pending = b.scheduled.filter(s => s.status === 'pending')
        .sort((a, b2) => {
          const ta = new Date(a.send_at + (a.send_at.endsWith('Z') ? '' : 'Z')).getTime();
          const tb = new Date(b2.send_at + (b2.send_at.endsWith('Z') ? '' : 'Z')).getTime();
          return ta - tb;
        });
      upcomingTotal = pending.length;
      upcomingSends = pending.slice(0, 5);
    }
    if (b.hunt != null) hunt = b.hunt as HuntResponse;
    if (b.progress != null) progress = b.progress;
    if (b.outreach != null) marketingDocs = b.outreach as OutreachEntry[];
  }

  // Calendar — its OWN call (no longer in the bundle). A slow/hanging calendar
  // API must never block the rest of Today, so it loads + caches independently.
  async function loadCalendar() {
    const seq = ++calendarSeq;
    try {
      const data = await getUpcomingEvents(1);
      if (destroyed || seq !== calendarSeq) return;
      const todayStr = new Date().toISOString().slice(0, 10);
      calls = ((data.events) || []).filter((e: any) =>
        (e.start || '').slice(0, 10) === todayStr && (e.call_type || '') !== 'internal'
      );
      calendarUnavailable = false;
      try { viewCache?.setItem(TODAY_CALLS_KEY, JSON.stringify(calls)); } catch {}
    } catch {
      // Leave the (possibly cached) calls in place on failure.
      if (!destroyed && seq === calendarSeq) calendarUnavailable = true;
    }
  }

  async function loadAll() {
    const seq = ++bundleSeq;
    loadingDecisions = true;
    loadingDrafts = true;
    try {
      const b = withoutSentCards(await getTodayBundle());
      if (destroyed || seq !== bundleSeq) return;
      unavailableSections = VISIBLE_BUNDLE_SECTIONS.filter(section => section in b && b[section] == null);
      applyBundle(b);
      latestBundle = { ...latestBundle, ...Object.fromEntries(
        Object.entries(b).filter(([, value]) => value != null),
      ) } as TodayBundle;
      const unresolved = unavailableSections.some(section => (latestBundle as any)?.[section] == null);
      try {
        if (unresolved) viewCache?.removeItem(TODAY_CACHE_KEY);
        else viewCache?.setItem(TODAY_CACHE_KEY, JSON.stringify(latestBundle));
      } catch {}
    } catch {
      // Bundle itself failed (network / auth). Leave existing state
      // alone so the page doesn't blank — the next poll tick retries.
      if (!destroyed && seq === bundleSeq) unavailableSections = ['all'];
    }
    if (destroyed || seq !== bundleSeq) return;
    loadingDrafts = false;
    loadingDecisions = false;
    initialLoadDone = true;
  }

  onMount(() => {
    // 1. Paint last-known state immediately from cache (stale).
    try {
      viewCache?.removeItem('today_bundle_v1');
      viewCache?.removeItem('today_calls_v1');
      const cached = viewCache?.getItem(TODAY_CACHE_KEY);
      if (cached) { latestBundle = JSON.parse(cached); applyBundle(latestBundle!); initialLoadDone = true; }
      const cachedCalls = viewCache?.getItem(TODAY_CALLS_KEY);
      if (cachedCalls) calls = JSON.parse(cachedCalls);
    } catch {}
    // 2. Revalidate bundle + calendar independently (calendar can't block Today).
    loadAll();
    loadCalendar();
  });
  // Iterative refresh while a poll is in progress — same pattern as Dashboard.
  // The teardown is load-bearing: Today unmounts on every view switch (App.svelte
  // renders the pages behind an `{#if}` chain), and the previous version held the
  // handle in a component-level `let` with no cleanup — so leaving Today mid-sync
  // stranded a 3s /today/bundle poller for the rest of the SPA session, and each
  // later visit-during-sync added another. Returning the disposer ties the
  // interval's lifetime to the effect (and therefore to the component).
  $effect(() => {
    if (!$pollingActive) return;
    const tick = setInterval(loadAll, 3000);
    return () => clearInterval(tick);
  });
  $effect(() => { if ($pollVersion > 0) loadAll(); });

  // ─── Sticky pill-nav: scrollspy + smooth jump ───
  // Shared with the Fund workspace, which renders the same TodayNav. The
  // observer config and `.today-section`'s scroll-margin-top are one unit —
  // see lib/sectionSpy.svelte.ts.
  const spy = createSectionSpy();
  const spyOn = spy.spyOn;

  // ─── helpers ───

  // Scoreboard line colors — one per teammate
  /* Series colours for the hunt chart. These are the same six hues as `STAGE_COLORS` and the
     avatar palette (`lib/stores.ts`) — same family, on purpose — but they are NOT that map and
     must not be aliased to it. A chart needs N mutually distinguishable lines; a stage colour
     carries a meaning, and §1 constrains it. Retuning one for its own reason must not silently
     recolour the other. Deliberately left as its own list; see UPDATES 2026-08-17. */
  const HUNT_LINE_COLORS = ['#E15D3D', '#6EA8F0', '#4ADE80', '#B689E0', '#F5B042', '#5EE2D0'];

  function relativeTime(iso: string): string {
    if (!iso) return '';
    const ts = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
    const diff = Date.now() - new Date(ts).getTime();
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  }

  // "May 30 (14d ago)" — the date + relative-time pattern that Critical's
  // original `.ctask-why` line used. Reused as the 3rd line across all the
  // Today tile families so Critical, Re-engage, and Respond stay visually
  // synchronized. Pass an age in days to skip the parse; useful when the
  // backend already computed `age_days` (going_cold, overdue) and the
  // string would otherwise round to the same number anyway.
  function dateWithAgo(dateStr: string, ageDays?: number): string {
    if (!dateStr) return '';
    const d = new Date(dateStr.length === 10 ? dateStr + 'T00:00:00Z' : dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const pretty = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const days = typeof ageDays === 'number'
      ? ageDays
      : Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
    return `${pretty} (${days}d ago)`;
  }

  function formatCallTime(iso: string): string {
    try {
      return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } catch { return iso; }
  }

  // "Alice, Bob, Carol +5 more" — compact attendee line so a big invite list
  // doesn't wrap into a wall of names in the meeting context block.
  function nameList(names: string[] | undefined, max = 3): string {
    if (!names || names.length === 0) return '';
    if (names.length <= max) return names.join(', ');
    return names.slice(0, max).join(', ') + ` +${names.length - max} more`;
  }

  // "Mon Jun 14, 10:00 AM · 45 min" — date + time + duration for the
  // meeting_followup "From your meeting" context block. Time reuses
  // formatCallTime so it renders identically to the same event in "Calls
  // today"; duration is dropped when the end is missing/unparseable, and the
  // time is dropped for a date-only value.
  function formatMeeting(startIso: string, endIso?: string): string {
    if (!startIso) return '';
    // Date-only values ("2026-07-20", all-day meetings) parse as UTC midnight
    // via `new Date(...)`, so `toLocaleDateString` renders the PREVIOUS day for
    // any timezone west of UTC. Parse the calendar parts as LOCAL midnight so
    // the displayed day matches the invite. Datetime values keep native parsing.
    const start = startIso.length <= 10
      ? (() => { const [y, m, d] = startIso.split('-').map(Number); return new Date(y, (m || 1) - 1, d || 1); })()
      : new Date(startIso);
    if (isNaN(start.getTime())) return startIso;
    const day = start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    const parts = [startIso.length > 10 ? `${day}, ${formatCallTime(startIso)}` : day];
    if (endIso) {
      const end = new Date(endIso);
      if (!isNaN(end.getTime())) {
        const mins = Math.round((end.getTime() - start.getTime()) / 60000);
        if (mins > 0) {
          const h = Math.floor(mins / 60), m = mins % 60;
          parts.push(h ? (m ? `${h} hr ${m} min` : `${h} hr`) : `${m} min`);
        }
      }
    }
    return parts.join(' · ');
  }

  // ─── George briefing actions ───
  async function markRead(id: string) {
    await markGeorgeMessageRead(id);
    invalidateTodayCache('george_messages');
    const msg = georgeMessages.find(m => m.id === id);
    if (msg) msg.read = 1;
  }
  async function markAllRead() {
    await markAllGeorgeRead();
    invalidateTodayCache('george_messages');
    georgeMessages.forEach(m => m.read = 1);
  }
  function discuss(msg: GeorgeMessage) {
    if (!msg.read) markRead(msg.id);
    $georgeDiscussContext = msg.content;
    $currentView = msg.client || 'prospecting';
  }
  const msgTypeBorder: Record<string, string> = {
    score_surge: 'var(--green)',
    score_drop: 'var(--red)',
    post_call: 'var(--blue)',
    dry_funnel: '#f59e0b',
    welcome: 'var(--accent)',
    build_complete: 'var(--accent)',
    overdue: 'var(--red)',
  };
  // Short, scannable title per George note — shown collapsed.
  const GEORGE_LABEL: Record<string, string> = {
    post_call: 'Post-call recap',
    score_surge: 'Momentum building',
    score_drop: 'Relationship weakening',
    dry_funnel: 'Funnel running dry',
    welcome: 'Welcome aboard',
    build_complete: 'Setup complete',
    overdue: 'Account slipping',
  };
  // Short tag shown in the tile pill.
  const GEORGE_TAG: Record<string, string> = {
    post_call: 'Call',
    score_surge: 'Momentum',
    score_drop: 'Cooling',
    dry_funnel: 'Funnel',
    welcome: 'Welcome',
    build_complete: 'Setup',
    overdue: 'Slipping',
  };
  // One-line preview of a note's body — skips the greeting, separators and the
  // bold subject header so the tile shows the first real sentence.
  function georgePreview(content: string): string {
    // Find the first meaningful line(s) of the note. Skip greetings,
    // headers, and separators. Return UNTRUNCATED — CSS line-clamp on
    // .ctask-why handles visual truncation with a clean word-boundary
    // ellipsis at the end of line 3 (the previous JS mid-string slice
    // was cutting words in half).
    const lines: string[] = [];
    for (const raw of (content || '').split('\n')) {
      const t = raw.trim();
      if (!t || /^-+$/.test(t)) continue;
      if (/^(hi|hello|hey|daniel)\b/i.test(t)) continue;
      if (/^\*\*.*\*\*$/.test(t)) continue;
      const clean = t.replace(/[*_`#>]/g, '').replace(/^[-\s]+/, '').trim();
      if (clean) {
        lines.push(clean);
        // ~600 chars is more than enough for 3 lines at the briefing
        // tile width; stop accumulating beyond that.
        if (lines.join(' ').length > 600) break;
      }
    }
    return lines.join(' ') || 'Open to read the full note.';
  }
  function toggleGeorge(m: GeorgeMessage) {
    const open = !expandedGeorge[m.id];
    expandedGeorge = { ...expandedGeorge, [m.id]: open };
    if (open && !m.read) markRead(m.id);
  }

  // ─── Awaiting reply actions ───
  // A threaded reply goes out under the thread's subject (inbox.py `_thread_reply_subject`); the model's may differ.
  function replySubjectFor(d: InboundDraft): string {
    const subject = (d.inbound_subject || '').trim();
    if (!d.thread_id || !subject) return d.draft_subject;
    return /^re:/i.test(subject) ? subject : `Re: ${subject}`;
  }

  async function startEditDraft(d: InboundDraft) {
    expandedDraft[d.id] = true;
    editingDraftId = d.id;
    editingDraftSubject = replySubjectFor(d);
    editingDraftBody = d.draft_body;
    editingDraftCustomFiles = [];
    polishChanges = '';

    // Load contact lists for chip pickers in parallel with splitting the
    // default To/CC into the three buckets. If contacts haven't loaded
    // yet when we split, an address falls through to "other"; when the
    // lists resolve we re-bucket once.
    const [clientList, internalList] = await Promise.all([
      getClientContacts(d.client).catch(() => [] as string[]),
      getInternalContacts().catch(() => [] as string[]),
    ]);
    if (editingDraftId !== d.id) return;
    editingClientContacts = clientList;
    editingInternalContacts = internalList;

    const clientSet = new Set(clientList.map(e => e.toLowerCase()));
    const internalSet = new Set(internalList.map(e => e.toLowerCase()));
    const bucket = (addrs: string[]) => {
      const c: string[] = []; const i: string[] = []; const o: string[] = [];
      for (const addr of addrs) {
        const lc = addr.toLowerCase();
        if (clientSet.has(lc)) c.push(addr);
        else if (internalSet.has(lc)) i.push(addr);
        else o.push(addr);
      }
      return { c, i, o };
    };

    // TO has no internal picker — anything we'd have bucketed as "internal"
    // folds into "other" so the address still rides on the email but the
    // user sees / edits it as free text.
    const toDefault = (d.reply_to_default || d.from_addr || '')
      .split(',').map(s => s.trim()).filter(Boolean);
    const toBucketed = bucket(toDefault);
    editingDraftToClient = toBucketed.c;
    editingDraftToOther = [...toBucketed.i, ...toBucketed.o].join(', ');

    const ccDefault = (d.reply_cc_default || '')
      .split(',').map(s => s.trim()).filter(Boolean);
    const ccBucketed = bucket(ccDefault);
    editingDraftCcClient = ccBucketed.c;
    editingDraftCcInternal = ccBucketed.i;
    editingDraftCcOther = ccBucketed.o.join(', ');
  }
  function cancelEditDraft() {
    editingDraftId = null;
    editingDraftSubject = '';
    editingDraftBody = '';
    editingDraftToClient = [];
    editingDraftToOther = '';
    editingDraftCcClient = [];
    editingDraftCcInternal = [];
    editingDraftCcOther = '';
    editingClientContacts = [];
    editingInternalContacts = [];
    editingDraftCustomFiles = [];
    polishChanges = '';
  }
  // Merge a chip-triple (client / internal / other) into a single
  // comma-joined string, case-insensitively deduped. Used for both To
  // and CC on send.
  function mergeTriple(c: string[], i: string[], other: string): string {
    const otherAddrs = other.split(',').map(s => s.trim()).filter(Boolean);
    const seen = new Set<string>();
    const out: string[] = [];
    for (const addr of [...c, ...i, ...otherAddrs]) {
      const lc = addr.toLowerCase();
      if (seen.has(lc)) continue;
      seen.add(lc);
      out.push(addr);
    }
    return out.join(', ');
  }
  // ─── Polish / Refine / Regenerate ───
  // All three operate on the open edit panel (so the user can iterate on the
  // draft inline) and overwrite editingDraftSubject + editingDraftBody on
  // success. Polish = small wording fixes. Refine = larger restructuring.
  // Regenerate = re-run the inbound-reply generator from scratch.
  let polishingDraftId = $state<string | null>(null);
  // Polish/Refine return a short "what changed" note from Claude. Render it
  // beneath the body editor (mirrors ClientView + Prospecting) so the user
  // can sanity-check the edits before sending. Cleared on every new action.
  let polishChanges = $state('');
  async function polishOrRefine(d: InboundDraft, mode: 'polish' | 'refine') {
    if (editingDraftId !== d.id) return;
    polishingDraftId = d.id;
    polishChanges = '';
    try {
      const r = await polishEmail({
        client: d.client, body: editingDraftBody, subject: editingDraftSubject, mode,
      });
      editingDraftBody = r.body;
      if (!d.thread_id) editingDraftSubject = r.subject;
      polishChanges = r.changes || '';
    } catch (e: any) {
      polishChanges = `${mode} failed: ${e?.message || e}`;
    }
    polishingDraftId = null;
  }
  async function regenerateDraft(d: InboundDraft) {
    if (editingDraftId !== d.id) return;
    polishingDraftId = d.id;
    polishChanges = '';
    try {
      const r = await regenerateAwaitingReply(d.id);
      invalidateTodayCache('drafts');
      if (!d.thread_id) editingDraftSubject = r.subject;
      editingDraftBody = r.body;
      polishChanges = 'Draft regenerated from scratch.';
      // Also reflect the new content on the underlying draft row in-memory
      // so collapsing + re-opening shows the fresh version.
      const idx = allDrafts.findIndex(x => x.id === d.id);
      if (idx >= 0) {
        allDrafts[idx] = { ...allDrafts[idx],
          draft_subject: d.thread_id ? d.draft_subject : r.subject, draft_body: r.body };
      }
    } catch (e: any) {
      polishChanges = `Regenerate failed: ${e?.message || e}`;
    }
    polishingDraftId = null;
  }

  // ─── Discuss ───
  // Route to ClientView with the auto-drafted reply pre-loaded into the
  // thread reply composer (replyDraftSeed), and seed the Strategize chat
  // with a discussion prompt about the inbound + the proposed reply so the
  // user can iterate in chat or just edit + send the pre-drafted email.
  function discussDraft(d: InboundDraft) {
    const cli = d.client.replace(/_/g, ' ');
    const inbound = `Subject: ${d.inbound_subject}\n\n${d.inbound_snippet || ''}`;
    const proposed = `Subject: ${replySubjectFor(d)}\n\n${d.draft_body}`;
    $chatSeed = {
      label: `Discuss reply — ${cli}`,
      // Strategic framing — the draft was already produced with full
      // context, so the value of this chat isn't to critique sentences;
      // it's to think about whether replying-and-waiting is the right
      // play vs. a different move (push for a meeting, harden a
      // different contact, slow-play it, escalate, etc.).
      prompt: `${cli} just emailed. Help me think through whether replying here is the right move at all, and if so, what the strongest version looks like.

--- Their message ---
${inbound}

--- The default reply I'd ship if I didn't think about it ---
${proposed}

What I want from you:
- What's actually happening with this account right now? What's the trajectory and where's the friction?
- Is sending the default reply (or a variant) the strongest move here, or is there a better play I should be making — push for a meeting, re-engage a different contact, send something more substantive, sit on it for a week, escalate?
- If the default reply is roughly right, anything strategic to layer in (a specific offer, an introduction, a piece of insight) that would do more work than just "no problem, take your time"?

Be direct. If you'd do something different, say so plainly. The reply composer in the Emails tab has the default draft pre-loaded — I'll port your edits or write something fresh after we think it through.`,
    };
    // We don't have client/internal contact lists here. Pass the full reply-
    // all CC as a single comma-joined string; ClientView's $replyDraftSeed
    // effect splits it into the three buckets against the client/internal
    // address books once they're loaded.
    $replyDraftSeed = {
      client: d.client,
      threadId: d.thread_id,
      to: d.reply_to_default || d.from_addr,
      ccClient: [],
      ccInternal: [],
      ccOther: d.reply_cc_default || '',
      subject: replySubjectFor(d),
      body: d.draft_body,
      brief: (d as any).brief || '',
    };
    $currentView = d.client;
  }

  async function sendDraft(d: InboundDraft) {
    sendingDraftId = d.id;
    try {
      const editing = editingDraftId === d.id;
      const subj = editing ? editingDraftSubject : undefined;
      const body = editing ? editingDraftBody : undefined;
      const to = editing
        ? mergeTriple(editingDraftToClient, [], editingDraftToOther)
        : undefined;
      const cc = editing
        ? mergeTriple(editingDraftCcClient, editingDraftCcInternal, editingDraftCcOther)
        : undefined;
      await sendAwaitingReply(d.id, subj, body, to, cc,
        editing && editingDraftCustomFiles.length
          ? editingDraftCustomFiles : undefined,
      );
      sentDraftIds.add(d.id);
      invalidateTodayCache('drafts');
      allDrafts = allDrafts.filter(x => x.id !== d.id);
      editingDraftId = null;
    } catch (e: any) {
      alert(`Send failed: ${e?.message || e}`);
    }
    sendingDraftId = null;
  }
  // ─── Decision actions ───
  async function doDecisionPrimary(d: Decision) {
    if (d.type === 'schedule_conflict') {
      $currentView = 'george';
    } else if (d.type === 'behind_target') {
      $currentView = 'prospecting';
    } else {
      // overdue / going_cold / score_drop / negative_reply / positive_no_followup
      $currentView = d.client;
    }
  }
  async function doDecisionSnooze(d: Decision, days: number) {
    // Snooze pushes the actual follow_up date forward instead of layering a
    // separate `decision_dismissals` snooze on top. Cleaner semantically —
    // the deal really is paused, the dashboard reflects that, and the next
    // sync naturally clears any proactive draft because the overdue
    // condition no longer holds. Pseudo-clients (calendar conflicts,
    // weekly-target) don't have a follow_up date — fall back to the
    // dismissal table for those.
    const realClient = !!d.client && d.client !== '(weekly)' && d.client !== '(calendar)';
    // Every sibling handler (dismissChase / putOnHold / skipProactive) reports a
    // failed write; this one didn't, so a rejected setFollowup threw past the
    // caller, left the card in place, and told the user nothing.
    try {
      if (realClient) {
        await setFollowup(d.client, todayPlusDays(days));
      } else {
        await dismissDecision(d.client, d.type, days);
      }
      invalidateTodayCache('decisions', 'dashboard');
    } catch (e: any) {
      alert(`Snooze failed: ${e?.message || e}`);
      return;
    }
    allDecisions = allDecisions.filter(x => !(x.client === d.client && x.type === d.type));
  }
  async function putOnHold(d: Decision) {
    // Capture the client's previous stage BEFORE flipping it so Undo can
    // restore it precisely, so Undo (the back-tile) can put the client
    // back in their prior lifecycle stage rather than defaulting.
    // Fallback is 'Engaged' — a REAL stage. The previous fallback, 'Engaged Up',
    // is the name of today.py's gate SET, not a member of it: `set_stage` does no
    // validation (state_db.py), so undoing a Put-on-Hold for a client the $stages
    // store hadn't populated wrote a stage that matches nothing in
    // storage.py:CLIENT_STAGES — and, because it isn't in ENGAGED_UP either, the
    // client then silently dropped out of the decision feed entirely. 'Engaged' is
    // the lowest rung of the bucket these cards are drawn from.
    const prevStage = ($stages as Record<string, string>)[d.client] || 'Engaged';
    try {
      await setStage(d.client, 'On Hold');
      invalidateTodayCache('decisions', 'dashboard');
      setClientStage(d.client, 'On Hold');
      dismissedTiles.set(decisionKey(d), {
        kind: 'put-on-hold', client: d.client, type: d.type, prevStage,
      });
      dismissedTiles = new Map(dismissedTiles);
    } catch (e) {
      alert('Put on Hold failed');
    }
  }

  // State-aware Dismiss for Chase tiles (overdue + similar). Stamps a
  // 7-day dismissal anchored to the client's current thread fingerprint,
  // identical semantics to the swipe-right Dismiss. Auto-expires when
  // the thread state advances (new inbound or outbound), so a real
  // re-engagement signal pops the tile back. The dismissedTiles map
  // gives an in-place Undo until the user refreshes.
  async function dismissChase(d: Decision) {
    try {
      await dismissDecision(d.client, d.type, 7, /* state_based */ true);
      invalidateTodayCache('decisions');
      const key = decisionKey(d);
      dismissedTiles.set(key, { kind: 'decision', client: d.client, type: d.type });
      dismissedTiles = new Map(dismissedTiles);
    } catch (e) {
      alert('Dismiss failed');
    }
  }

  // ─── Re-engagement / hidden-prompt actions ───
  // Shared problem statement used by Re-engage (going_cold), Critical's
  // overdue button, and Briefing's score_drop "Plan a recovery". Callers
  // supply the situation-specific opening; the rest of the framing is the
  // same — a problem statement, not a recipe, so George decides the play.
  function buildReengagementPrompt(client: string, situation: string): string {
    const name = client.replace(/_/g, ' ');
    return `${name} ${situation}. I want to get this ${$terms.counterparty} re-engaged.

Work it out. Don't assume the move is just chasing the same person on the same thread — weigh that against what actually happened on this account:

• The contact who went quiet may have gone cold for a reason. It can be worth opening a fresh thread with a different person at ${name} — a peer, their manager, or an adjacent team — rather than waiting on the one who went silent.
• The angle that stalled may need to change. A different hook — a new development on our side, a different outcome to lead with, a smaller and easier ask — can reopen a conversation a straight follow-up won't.
• Sometimes it comes down to a concrete trigger or timing, not persistence.

Look at the real history with ${name} — who we've spoken to, what was last discussed, where it stalled — decide which of these (or what combination) gives the best shot, explain your reasoning briefly, then draft the outreach you'd actually send.`;
  }
  function reengage(d: Decision) {
    const days = d.age_days || 0;
    const situation = days > 0
      ? `has gone quiet — no reply in ${days} days, on a relationship that was active`
      : 'has gone quiet, on a relationship that was active';
    $chatSeed = {
      label: `Re-engagement strategy — ${d.client.replace(/_/g, ' ')}`,
      prompt: buildReengagementPrompt(d.client, situation),
    };
    $currentView = d.client;
  }

  // ─── Lookalike-prospect actions (dry_funnel structured payload) ───
  // Two per-prospect actions:
  //   + → standard CRM ingest. Reuses POST /contacts (the same code path
  //     that creates clients from manual contact entry). If the domain
  //     is already mapped to a client, the contact attaches there;
  //     otherwise a new client is created and stamped Prospect stage.
  //   Prospect → opens the Prospecting tab pre-seeded with this target
  //     (company / contact / brief), where George's chat panel takes
  //     over for angle-finding before outreach.
  // Tracks which contacts have already been ingested into the CRM,
  // keyed by `email|company`. Seeded from the server's `in_crm` flag
  // on each dry_funnel prospect (set at list-time via a single batched
  // SELECT) so the ✓ state survives page refresh. Subsequent in-tab
  // adds populate this in real time as ingestProspect() succeeds.
  let prospectAddedIds = $state<Record<string, true>>({});
  $effect(() => {
    const seeded: Record<string, true> = {};
    for (const m of georgeMessages) {
      if (m.msg_type !== 'dry_funnel') continue;
      const ps = m.payload?.prospects;
      if (!ps) continue;
      for (const p of ps) {
        if (p.in_crm) seeded[`${p.email}|${p.company}`] = true;
      }
    }
    // Merge with any in-flight client-side additions so we don't undo a
    // just-clicked ✓ while georgeMessages refreshes from a poll.
    // `prospectAddedIds` is read via untrack(): this effect must depend ONLY
    // on georgeMessages. Reading it tracked while also writing it here made the
    // effect re-trigger on its own write → effect_update_depth_exceeded, which
    // wedged the page's reactive scheduler (dismissals stopped hiding cards).
    prospectAddedIds = { ...seeded, ...untrack(() => prospectAddedIds) };
  });
  async function ingestProspect(p: import('../lib/api').LookalikeProspect) {
    const key = `${p.email}|${p.company}`;
    if (prospectAddedIds[key]) return;
    try {
      const { createContact } = await import('../lib/api');
      await createContact({
        email: p.email, name: p.person_name, title: p.role,
        phone: '', client: p.company, tag: 'Dealton-generated',
      });
      prospectAddedIds = { ...prospectAddedIds, [key]: true };
    } catch (err) {
      console.error('ingestProspect failed', err);
      alert('Could not add to contacts — see console.');
    }
  }
  function prospectLookalike(p: import('../lib/api').LookalikeProspect) {
    $prospectingTileSeed = {
      client: p.company,
      contactEmail: p.email,
      contactName: p.person_name,
      subject: '',
      body: '',
      brief: p.how_to_approach || p.why_fits || '',
    };
    $currentView = 'prospecting';
  }
  // Group prospects by company so the brief renders one section per
  // company (with N contact rows under it) instead of N duplicate
  // company headings. Preserves order of first appearance.
  type ProspectGroup = {
    company: string;
    contacts: import('../lib/api').LookalikeProspect[];
    why_fits: string;
    how_to_approach: string;
  };
  function groupByCompany(prospects: import('../lib/api').LookalikeProspect[]): ProspectGroup[] {
    const groups: ProspectGroup[] = [];
    const idx: Record<string, number> = {};
    for (const p of prospects) {
      if (idx[p.company] === undefined) {
        idx[p.company] = groups.length;
        groups.push({ company: p.company, contacts: [], why_fits: p.why_fits, how_to_approach: p.how_to_approach });
      }
      groups[idx[p.company]].contacts.push(p);
    }
    return groups;
  }
  // Per-company "Make first contact →" button. Chat-first workflow:
  // seeds George's chat panel with all the company context (the angle,
  // the candidate contacts, the suggested hook) and navigates to
  // Prospecting. Does NOT pre-fill the Generate form — the user picks
  // the target after talking through the approach with George, then
  // chat's "Draft email ↓" uses the conversation as the brief.
  function prospectCompany(g: ProspectGroup) {
    if (!g.contacts.length) return;
    const agent = $agentName || 'George';
    const contactList = g.contacts
      .map(c => `- ${c.person_name} (${c.role}) — ${c.email}`)
      .join('\n');
    $chatSeed = {
      label: `Make first contact — ${g.company}`,
      prompt: `${agent} surfaced ${g.company} as a lookalike prospect with no existing relationship. Help me think through how to best open this account.

Why this fits the profile: ${g.why_fits}

Suggested angle: ${g.how_to_approach}

Named contacts ${agent} found:
${contactList}

What I want from you:
- Which of these contacts is the strongest entry point, or is there someone better not on this list?
- Is the suggested angle the right one, or is there a sharper hook (a specific project, recent news, a different signal)?
- Should I be sending a cold reach at all to this account, or is there a warmer route (a referral, a different channel, a wait-and-watch)?

Be direct. If you'd take a different approach entirely, say so.`,
    };
    $currentView = 'prospecting';
  }

  // ─── Briefing per-type primary action ───
  // Client-tagged briefings (post_call / score_surge / score_drop) drop into
  // the client's chat with a hidden, situation-specific prompt. Navigation
  // briefings (dry_funnel / welcome / build_complete) navigate AND dismiss.
  function briefAction(m: GeorgeMessage) {
    const t = m.msg_type;
    const name = (m.client || '').replace(/_/g, ' ');
    // George's actual briefing body — surfaced verbatim into the chat
    // prompt so the conversation starts with the same context the rep
    // just read. Mirrors how the Respond/Overdue/Gone Cold Discuss
    // handlers inject the proposed draft into chat. Truncated for prompt
    // budget; the full content lives in george_messages.
    const ctx = (m.content || '').slice(0, 1400);
    // Per-user agent name (each tenant configures their own — 'Dealton'
    // is the default when nothing's set). Resolved from the $agentName
    // store so the chat prompt addresses the right persona.
    const agent = $agentName || 'Dealton';
    if (t === 'score_drop' && m.client) {
      $chatSeed = {
        label: `Plan a recovery — ${name}`,
        prompt: `${name}'s engagement score just dropped — ${agent} flagged it. Here's the briefing:

--- ${agent}'s note ---
${ctx}

What I want from you:
- What's actually happening with this account right now? Where's the friction?
- Is a direct re-engagement the right move, or is there a stronger play — a different contact, a new angle, a substantive value-add, escalating, or letting it die?
- If outreach is right, draft the email I should send. Make the ask concrete — no vague "worth a quick chat?".

Be direct. If you'd take a different approach entirely, say so. The Emails tab composer is open below the chat — I'll port your edits or write fresh after we think it through.`,
      };
      $replyDraftSeed = {
        client: m.client, threadId: '',
        to: '', ccClient: [], ccInternal: [], ccOther: '',
        subject: '', body: '',
        brief: `Re-engage a ${$terms.counterparty} whose engagement score has dropped. The email `
             + 'should NOT mention the score itself; lead with a substantive '
             + 'value-add (a new piece of context, a specific resource, a '
             + 'concrete next step we can advance to). Warm professional tone, '
             + "no manufactured CTAs ('just say the word', 'happy to chat anytime'). "
             + 'End naturally.',
      };
      $currentView = m.client;
    } else if (t === 'post_call' && m.client) {
      $chatSeed = {
        label: `Post-call follow-up — ${name}`,
        prompt: `${name} — we just had a call. Here's ${agent}'s note from the recap:

--- ${agent}'s note ---
${ctx}

Draft the follow-up email I should send now. Lead with the substance, not pleasantries: name the specific takeaways, restate the concrete next step(s) we agreed on, and propose a precise time or deliverable. If a call is the right next step, ask for it concretely (a specific time or two) — never a vague "worth a quick call?". Don't manufacture a CTA if there is no real one to make; end naturally.`,
      };
      $replyDraftSeed = {
        client: m.client, threadId: '',
        to: '', ccClient: [], ccInternal: [], ccOther: '',
        subject: '', body: '',
        brief: 'Post-meeting follow-up. The meeting already happened — write '
             + 'a recap addressed to the call attendees with the specific '
             + 'takeaways, the concrete next steps we committed to, and any '
             + 'follow-up deliverables. Warm, slightly informal tone. No '
             + 'manufactured CTAs.',
      };
      $currentView = m.client;
    } else if (t === 'score_surge' && m.client) {
      $chatSeed = {
        label: `Make the next move — ${name}`,
        prompt: `${name} is showing strong momentum — ${agent} just flagged it. Here's the briefing:

--- ${agent}'s note ---
${ctx}

What I want from you:
- What actually moved on this account, and what's the right play to capitalise while it's hot?
- Substantive follow-up that builds on the moment? Request for a concrete next step? Introduction of a specific resource? Calendar block?
- Explain briefly which move you'd make and why, then draft the email I should send.

Make the ask concrete — never a vague "worth a quick chat?". The Emails tab composer is open below — I'll port your edits or write fresh after we think it through.`,
      };
      $replyDraftSeed = {
        client: m.client, threadId: '',
        to: '', ccClient: [], ccInternal: [], ccOther: '',
        subject: '', body: '',
        brief: `Press the advantage on a ${$terms.counterparty} showing strong momentum. `
             + 'The email should build on whatever just moved — propose a '
             + 'concrete next step (specific times for a call, a deliverable '
             + 'we can send, an introduction we can make). Confident but not '
             + 'pushy. No vague CTAs.',
      };
      $currentView = m.client;
    } else if (t === 'dry_funnel') {
      // dry_funnel dismissal is backend-driven (a newer dry_funnel supersedes,
      // or a real inbound ticks first_inbound_*). Don't dismiss on click.
      // Lands on the Prospecting page (not a client view), so no
      // replyDraftSeed — the prospecting flow builds its own composer.
      $chatSeed = {
        label: 'Plan how to thicken the funnel',
        prompt: `${agent} just flagged my pipeline as dry — not enough first-time inbound conversions in the past two weeks. Here's the briefing:

--- ${agent}'s note ---
${ctx}

What I want from you (two parts):

**1. Re-activate the existing pipeline.**
Look at the accounts I've been working, the accounts I've gone quiet on, and any obvious gaps. Tell me where to spend the next 30 minutes: which 2–3 specific existing accounts deserve outreach this week (name them), what kind of message would actually land for each, and the concrete first move I should make.

**2. Bring in new prospects.**
Research and propose 3–5 NEW accounts or NEW people I should reach out to — companies I'm NOT already tracking, OR additional contacts at companies I AM tracking. For each, tell me:
- Who specifically (company + named person + role)
- Why now (a recent event, a specific project, a public signal, a relevant trend)
- The opening hook that would actually land

Use whatever research tools you have — web search, public news, my prior outreach patterns, the product knowledge in my system — to ground the picks in something real. Don't invent contacts or facts; if you can't verify a name, say "an X at Y" instead.

Be specific — no generic prospecting advice. The top of the funnel only thickens with concrete new names.`,
      };
      $currentView = 'prospecting';
    } else if (t === 'overdue' && m.client) {
      $chatSeed = {
        label: `Re-engage — ${name}`,
        prompt: `${name} is slipping — the follow-up clock ran out and ${agent} flagged it. Here's the briefing:

--- ${agent}'s note ---
${ctx}

What I want from you:
- What actually happened on this account — where did momentum stall, and is the silence from one person or the whole side?
- Is chasing the same contact on the same thread the right move, or is there a stronger play — a different person at ${name}, a different angle (new development, smaller ask, concrete trigger), or letting it die quietly?
- If outreach is the move, draft the email I should send. Concrete ask, not "worth a quick chat?". If a different play is better, say so plainly.

The Emails tab composer is open below the chat — I'll port your draft or rewrite after we think it through.`,
      };
      $replyDraftSeed = {
        client: m.client, threadId: '',
        to: '', ccClient: [], ccInternal: [], ccOther: '',
        subject: '', body: '',
        brief: `Re-engage a ${$terms.counterparty} whose follow-up went past its due date and `
             + 'momentum has slipped. The email should not nag about the gap — '
             + 'lead with something substantive (a new development on our side, '
             + 'a fresh angle, a smaller and easier ask) that gives them a real '
             + 'reason to re-engage. Warm professional tone, no manufactured '
             + "CTAs ('just say the word', 'happy to chat anytime'). End naturally.",
      };
      $currentView = m.client;
    } else if (t === 'welcome') {
      try { dismissGeorgeMessage(m.id); } catch {}
      georgeMessages = georgeMessages.filter(x => x.id !== m.id);
      // Product Knowledge is now the Knowledge tab of Teach George.
      if (typeof window !== 'undefined') {
        history.replaceState({ view: 'teach' }, '', '/teach?tab=knowledge');
      }
      $currentView = 'teach';
    } else if (t === 'build_complete') {
      try { dismissGeorgeMessage(m.id); } catch {}
      georgeMessages = georgeMessages.filter(x => x.id !== m.id);
      $currentView = 'dashboard';
    } else {
      // Fallback for unknown types — preserve the old Discuss flow.
      discuss(m);
    }
  }
  // Per-type button label for the primary briefing action.
  const BRIEF_BTN: Record<string, string> = {
    post_call: 'Draft follow-up →',
    score_surge: 'Press the advantage →',
    score_drop: 'Plan a recovery →',
    dry_funnel: 'Prospect',
    welcome: 'Open Knowledge Base →',
    build_complete: 'Open Dashboard →',
    overdue: 'Re-engage →',
  };

  function decisionKey(d: Decision): string {
    return `${d.client}|${d.type}`;
  }

  // ─── Dismissed-with-undo state ───
  // When the user dismisses a tile (swipe, Dismiss button, Put on Hold),
  // the tile stays in the list but flips to a "back-of-tile" view
  // showing only the company name + an Undo button. The actual server
  // dismissal already happened — Undo reverses it. On reload, dismissed
  // items don't come back in the API response, so the in-page state
  // disappears naturally. No timer / no auto-fade — the dimmed state
  // persists until Undo or reload.
  //
  // The map value carries undo metadata so we can restore precisely:
  //   - decision dismiss: { kind: 'decision', client, type }
  //   - going_cold put-on-hold: also { prevStage } so we can restore the
  //     client's prior lifecycle stage on undo
  //   - brief: { kind: 'brief', id }
  //   - draft (inbound/proactive): undo not yet wired — out of scope for
  //     this pass; current sendDraft/sendProactive removes the row.
  type DismissedEntry =
    | { kind: 'decision'; client: string; type: string }
    | { kind: 'put-on-hold'; client: string; type: string; prevStage: string }
    | { kind: 'brief'; id: string }
    // Snapshot the full InboundDraft so Undo can restore it without
    // re-fetching the inbox bundle. Backend has the row in 'skipped'
    // state; Undo calls unskipAwaitingReply to flip it back to 'pending'.
    | { kind: 'draft'; id: string; draft: InboundDraft };
  let dismissedTiles = $state<Map<string, DismissedEntry>>(new Map());

  async function undoDismiss(key: string) {
    const entry = dismissedTiles.get(key);
    if (!entry) return;
    try {
      if (entry.kind === 'decision') {
        await undismissDecision(entry.client, entry.type);
      } else if (entry.kind === 'put-on-hold') {
        // Restore the client's previous lifecycle stage. Falls back to
        // 'Engaged' if we didn't capture one (defensive — the swipe
        // path always captures, but the button path might not). Must stay a
        // real CLIENT_STAGES value: `set_stage` accepts any string, so a
        // placeholder here persists as an unmatchable stage. See putOnHold.
        await setStage(entry.client, entry.prevStage || 'Engaged');
        setClientStage(entry.client, entry.prevStage || 'Engaged');
      } else if (entry.kind === 'brief') {
        await undismissGeorgeMessage(entry.id);
      } else if (entry.kind === 'draft') {
        // Flip the inbound_draft back to 'pending' on the server. The
        // draft never left `drafts` (same convention as decisionCard
        // etc — dismissedTiles handles the visual swap). Removing the
        // dismissedTiles entry below restores the full-card render.
        const { unskipAwaitingReply } = await import('../lib/api');
        await unskipAwaitingReply(entry.id);
        // Defensive: if a parallel refresh dropped the draft from the
        // live list, restore it from the snapshot.
        if (!allDrafts.some(d => d.id === entry.id)) {
          allDrafts = [entry.draft, ...allDrafts];
        }
      }
      invalidateTodayCache(entry.kind === 'draft' ? 'drafts' : entry.kind === 'brief' ? 'george_messages' : 'decisions');
    } catch {}
    dismissedTiles.delete(key);
    dismissedTiles = new Map(dismissedTiles);  // trigger reactivity
  }

  function todayPlusDays(days: number): string {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return localDay(d);
  }

  // ─── Scheduled actions ───
  async function cancelSend(id: string) {
    if (!confirm('Cancel this scheduled send?')) return;
    await cancelScheduledEmail(id);
    invalidateTodayCache('scheduled');
    upcomingSends = upcomingSends.filter(s => s.id !== id);
  }

  // ─── Calls actions ───
  async function toggleCallInvite(eventId: string, currentlyInvited: boolean) {
    try {
      if (currentlyInvited) await removeGeorgeFromEvent(eventId);
      else await inviteGeorgeToEvent(eventId);
      ++calendarSeq;
      try { viewCache?.removeItem(TODAY_CALLS_KEY); } catch {}
      await loadCalendar();
    } catch (e: any) {
      alert(`Calendar update failed: ${e?.message || e}`);
    }
  }
</script>

<!-- ════ Dismissed back-of-tile — shared "tile flipped over" view ════
     Renders in place of any dismissed tile's normal content. Shows the label
     (company / brief title), the action taken ("Dismissed" vs "On hold" — read
     from the entry's kind so Put-on-Hold doesn't mislabel as Dismissed), and an
     Undo button. Stays until the user clicks Undo or reloads the page. -->
{#snippet dismissedBackTile(label: string, key: string)}
  {@const onHold = dismissedTiles.get(key)?.kind === 'put-on-hold'}
  <article class="dismissed-back-tile">
    <div class="dismissed-back-text">
      <span class="dismissed-back-label">{label}</span>
      <span class="dismissed-back-status">{onHold ? 'On hold' : 'Dismissed'}</span>
    </div>
    <button class="today-mini-btn dismissed-undo-btn"
            onclick={(e) => { e.stopPropagation(); undoDismiss(key); }}>
      ↶ Undo
    </button>
  </article>
{/snippet}


<!-- ════ Critical task tile — v10 style, numbered, accent by severity ════ -->
{#snippet criticalCard(d: Decision, n: number)}
  {@const key = decisionKey(d)}
  {@const realClient = !!d.client && d.client !== '(weekly)' && d.client !== '(calendar)'}
  {@const stage = realClient ? ($stages[d.client] || '') : ''}
  {@const headerName = realClient ? d.client.replace(/_/g, ' ') : d.title}
  {#if dismissedTiles.has(key)}
    {@render dismissedBackTile(headerName, key)}
  {:else if d.type === 'overdue'}
    <!-- Overdue-no-draft: mirror the COLLAPSED look of a with-draft chase card
         (proactiveDraftCard) — same subtitle + chevron + Snooze/Dismiss — so a
         missing draft doesn't advertise itself. Expands to an "Open client"
         panel (there's no draft to send). Same onclick/keyboard toggle
         semantics as proactiveDraftCard so the two behave identically. -->
    {@const open = !!expandedNoDraft[key]}
    <div class="ctask-swipe-wrap">
    <article class="draft-card sev-{d.severity}" class:open
             onclick={(e) => {
               if ((e.target as HTMLElement).closest('button, input, textarea, label, select, [contenteditable], [role="button"], .draft-inbound')) return;
               toggleNoDraft(key);
             }}>
      <!-- role+tabindex are what make the onkeydown reachable: without them the
           header can't take focus, so the handler never fired and this card was
           mouse-only. Matches the Respond card's header. -->
      <!-- onclick is load-bearing, not redundant with the article handler: that
           handler skips `[role="button"]`, and this header declares that role
           (added so the keydown below is reachable). Without its own onclick
           the whole head — number, name, stage pill, chevron — is mouse-dead. -->
      <header class="draft-head" role="button" tabindex="0"
              onclick={() => toggleNoDraft(key)}
              onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleNoDraft(key); } }}>
        <span class="draft-num">{n}</span>
        <div class="draft-head-body">
          <div class="draft-head-row">
            <span class="draft-client">{headerName}</span>
            {#if stage}
              <span class="draft-stage-pill" style="color:{stageColor(stage)};border-color:color-mix(in srgb, {stageColor(stage)} 40%, transparent);">{$stageLabel(stage)}</span>
            {/if}
            <span class="draft-chev" class:open>›</span>
          </div>
          <div class="draft-subtitle">{d.title}</div>
          <div class="draft-subject-line">{d.context}</div>
        </div>
        <div class="draft-actions inline-right">
          <button class="today-mini-btn" onclick={(e) => { e.stopPropagation(); doDecisionSnooze(d, 7); }}>Snooze 7d</button>
          {#if !open}
          <button class="today-mini-btn ghost" onclick={(e) => { e.stopPropagation(); dismissChase(d); }}>Dismiss</button>
          {/if}
        </div>
      </header>
      {#if open}
        <!-- Grey .draft-inbound shell holds ONLY the note (like the draft card's
             Past-thread context block). The ACTIONS live in the outer footer
             below — same slot the with-draft card uses for Send/Edit/Discuss/
             Dismiss — NOT inside the grey box. -->
        <div class="draft-inbound">
          <span>No draft was written for this follow-up yet — open the {$terms.counterparty} to pick up the thread.</span>
        </div>
        <div class="draft-actions">
          <!-- draft-discuss-btn (not draft-navlink): orange, NO underline — matches
               the "Discuss →" link. Dismiss ghost is pinned right by the
               `.draft-actions:not(.inline-right) > .ghost { margin-left:auto }` rule. -->
          <button class="today-mini-btn draft-discuss-btn" onclick={(e) => { e.stopPropagation(); doDecisionPrimary(d); }}>Open {$terms.counterparty} →</button>
          <button class="today-mini-btn ghost" onclick={(e) => { e.stopPropagation(); dismissChase(d); }}>Dismiss</button>
        </div>
      {/if}
    </article>
    </div>
  {:else}
  <!-- Fallback Critical tile (no proactive draft): row 2 = d.title, row 3 =
       d.context (the original strings — preserves the "Follow-up was due
       {date} ({Nd} ago)" phrasing the prior layout had). Actions render to
       the RIGHT of the head body, matching the original .ctask layout. -->
  <div class="ctask-swipe-wrap">
  <article class="draft-card sev-{d.severity} critical-fallback actions-right">
    <header class="draft-head">
      <span class="draft-num">{n}</span>
      <div class="draft-head-body">
        <div class="draft-head-row">
          <span class="draft-client">{headerName}</span>
          {#if stage}
            <span class="draft-stage-pill" style="color:{stageColor(stage)};border-color:color-mix(in srgb, {stageColor(stage)} 40%, transparent);">{$stageLabel(stage)}</span>
          {/if}
        </div>
        <div class="draft-subtitle">{d.title}</div>
        <div class="draft-subject-line">{d.context}</div>
      </div>
      <div class="draft-actions inline-right">
        {#if d.type === 'positive_no_followup'}
          <button class="today-mini-btn draft-navlink" onclick={() => doDecisionPrimary(d)}>Set next step →</button>
          <button class="today-mini-btn ghost" onclick={() => doDecisionSnooze(d, 7)}>Snooze</button>
        {:else if d.type === 'negative_reply'}
          <button class="today-mini-btn draft-navlink" onclick={() => doDecisionPrimary(d)}>Try another angle →</button>
          <button class="today-mini-btn ghost" onclick={() => putOnHold(d)}>Close as Lost</button>
        {:else if d.type === 'schedule_conflict'}
          <button class="today-mini-btn draft-navlink" onclick={() => doDecisionPrimary(d)}>Open calendar →</button>
        {:else if d.type === 'behind_target'}
          <button class="today-mini-btn draft-navlink" onclick={() => doDecisionPrimary(d)}>Top up the funnel →</button>
          <button class="today-mini-btn ghost" onclick={() => doDecisionSnooze(d, 7)}>Snooze</button>
        {:else}
          <!-- Any type this chain does not name — a new backend decision type, or an old
               one after a rename — rendered an action row with NO buttons, so the card
               could not be cleared at all. Both handlers already cope with an unknown
               type (`doDecisionPrimary` falls through to opening the client), so this
               adds no new policy, only a way out. -->
          <button class="today-mini-btn draft-navlink" onclick={() => doDecisionPrimary(d)}>Open {$terms.counterparty} →</button>
          <button class="today-mini-btn ghost" onclick={() => doDecisionSnooze(d, 7)}>Snooze</button>
        {/if}
      </div>
    </header>
  </article>
  </div>
  {/if}
{/snippet}

<!-- ════ Briefing note tile — v10 style, numbered, accent by note type ════ -->
{#snippet georgeCard(m: GeorgeMessage, n: number)}
  {@const open = !!expandedGeorge[m.id]}
  {@const accent = msgTypeBorder[m.msg_type] || 'var(--border-strong)'}
  {@const btnLabel = BRIEF_BTN[m.msg_type] || 'Discuss'}
  {@const hasVerb = !(m.msg_type === 'dry_funnel' && m.payload?.kind === 'lookalike_prospects' && (m.payload.prospects?.length ?? 0) > 0)}
  {@const briefLabel = m.client ? m.client.replace(/_/g, ' ') : (GEORGE_LABEL[m.msg_type] || 'Update')}
  {#if dismissedTiles.has(m.id)}
    {@render dismissedBackTile(briefLabel, m.id)}
  {:else}
  <div class="ctask-swipe-wrap">
    <article class="ctask gnote-tile" class:unread={!m.read}
      class:open class:has-verb={hasVerb}
      style="--ctask-accent: {accent};"
      role="button" tabindex="0"
      onclick={() => toggleGeorge(m)}
      onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGeorge(m); } }}>
      <span class="ctask-num">{n}</span>
      <div class="ctask-body">
        <div class="gnote-namerow">
          {#if !m.read}<span class="gnote-dot" aria-label="unread"></span>{/if}
          <span class="gnote-name">{m.client ? m.client.replace(/_/g, ' ') : (GEORGE_LABEL[m.msg_type] || 'Update')}</span>
          <span class="draft-stage-pill" style="color:{accent};border-color:color-mix(in srgb, {accent} 40%, transparent);">{GEORGE_TAG[m.msg_type] || 'Note'}</span>
          <!-- Chevron sits BESIDE the title (mirrors Respond's .draft-chev), not
               pushed to the far-right corner above the action verb. -->
          <span class="ctask-chev">›</span>
          <!-- Timestamp only when expanded — declutters the collapsed row. -->
          {#if open}<span class="gnote-time">{relativeTime(m.created_at)}</span>{/if}
          {#if hasVerb}
            <button class="ctask-btn ctask-verb-right"
                    onclick={(e) => { e.stopPropagation(); briefAction(m); }}>{btnLabel}</button>
          {/if}
        </div>
        {#if open}
          <!-- stopPropagation so reading / clicking links inside the expanded
               body doesn't collapse the tile or trigger a swipe. -->
          {#if m.msg_type === 'dry_funnel' && m.payload?.kind === 'lookalike_prospects' && (m.payload.prospects?.length ?? 0) > 0}
            <div class="gnote-full dry-funnel-brief"
                 onclick={(e) => e.stopPropagation()}
                 onmousedown={(e) => e.stopPropagation()}>
              {#if m.payload.opener}
                <div class="dry-opener md-content">{@html renderSafeMarkdown(m.payload.opener)}</div>
              {/if}
              {#each groupByCompany(m.payload.prospects!) as g}
                <div class="dry-company">
                  <div class="dry-company-body">
                    <h4 class="dry-prospect-co">{g.company}</h4>
                    {#each g.contacts as p}
                      {@const addedKey = `${p.email}|${p.company}`}
                      <div class="dry-prospect-meta">
                        <strong>{p.person_name}</strong> · {p.role} · <span class="dry-prospect-email">{p.email}</span>
                        <button class="dry-prospect-add-icon"
                                title={prospectAddedIds[addedKey] ? 'Added to contacts' : 'Add as Prospect-stage contact'}
                                disabled={!!prospectAddedIds[addedKey]}
                                onclick={(e) => { e.stopPropagation(); ingestProspect(p); }}>{prospectAddedIds[addedKey] ? '✓' : '+'}</button>
                      </div>
                    {/each}
                    {#if g.why_fits}
                      <p class="dry-prospect-fit">{g.why_fits}</p>
                    {/if}
                    {#if g.how_to_approach}
                      <p class="dry-prospect-approach"><strong>How to approach:</strong> {g.how_to_approach}</p>
                    {/if}
                  </div>
                  <div class="dry-company-actions">
                    <button class="ctask-btn"
                            onclick={(e) => { e.stopPropagation(); prospectCompany(g); }}>Make first contact →</button>
                  </div>
                </div>
              {/each}
            </div>
          {:else}
            <div class="gnote-full md-content"
                 onclick={(e) => e.stopPropagation()}
                 onmousedown={(e) => e.stopPropagation()}>{@html renderSafeMarkdown(m.content)}</div>
          {/if}
        {:else}
          <div class="ctask-why">{georgePreview(m.content)}</div>
        {/if}
      </div>
      <div class="ctask-actions">
        <!-- No tile-level verb for the dry-funnel lookalike brief: a single
             "Make first contact →" is ambiguous when collapsed (which of the N
             companies?). The per-company buttons live in the EXPANDED brief;
             collapsed just expands (chevron / click). `hasVerb` is false for
             exactly this case, so nothing renders here. -->
        {#if hasVerb}
          <button class="ctask-btn" onclick={(e) => { e.stopPropagation(); briefAction(m); }}>{btnLabel}</button>
        {/if}
        <!-- Dismiss → the same dismissedTiles path (in-tile Undo back-of-tile). -->
        <button class="today-mini-btn ghost"
                onclick={async (e) => {
                  e.stopPropagation();
                  try {
                    await dismissGeorgeMessage(m.id);
                    invalidateTodayCache('george_messages');
                    dismissedTiles.set(m.id, { kind: 'brief', id: m.id });
                    dismissedTiles = new Map(dismissedTiles);
                  } catch {}
                }}>Dismiss</button>
      </div>
    </article>
  </div>
  {/if}
{/snippet}

<!-- ════ Re-engage fallback tile — same draft-card shell + sev-blue accent ════
     Used when a going_cold row has NO proactive draft attached. Mirrors
     criticalCard's structure: actions inline-right inside the head, no
     expandable body. -->
{#snippet reengageCard(d: Decision, n: number)}
  {@const key = decisionKey(d)}
  {@const stage = $stages[d.client] || ''}
  {@const row3 = d.follow_up
      ? `Gone cold on ${dateWithAgo(d.follow_up, d.age_days)}`
      : `${d.age_days}d silent`}
  {#if dismissedTiles.has(key)}
    {@render dismissedBackTile(d.client.replace(/_/g, ' '), key)}
  {:else}
  <div class="ctask-swipe-wrap">
  <article class="draft-card sev-blue critical-fallback actions-right">
    <header class="draft-head">
      <span class="draft-num">{n}</span>
      <div class="draft-head-body">
        <div class="draft-head-row">
          <span class="draft-client">{d.client.replace(/_/g, ' ')}</span>
          {#if stage}
            <span class="draft-stage-pill" style="color:{stageColor(stage)};border-color:color-mix(in srgb, {stageColor(stage)} 40%, transparent);">{$stageLabel(stage)}</span>
          {/if}
        </div>
        <div class="draft-subtitle">Gone cold</div>
        <div class="draft-subject-line">{row3}</div>
      </div>
      <div class="draft-actions inline-right">
        <!-- Gone cold (no draft): mirrors the with-draft going_cold card —
             Put on Hold is the primary action, Dismiss the secondary exit.
             dismissChase (not skipProactive) since there's no draft to skip. -->
        <button class="today-mini-btn" onclick={() => putOnHold(d)}>Put on Hold</button>
        <button class="today-mini-btn ghost" onclick={() => dismissChase(d)}>Dismiss</button>
      </div>
    </header>
  </article>
  </div>
  {/if}
{/snippet}

<!-- ════ Proactive draft tile — Respond-style markup with severity accent ════
     Used for Critical (overdue) + Re-engage (going_cold) when a proactive
     draft is attached on the row. Visual structure mirrors `draftCard` so
     the three buckets read as one family; only the left accent strip + the
     subtitle text differs by decision type. -->
{#snippet proactiveDraftCard(d: Decision, n: number)}
  {@const pd = d.proactive_draft!}
  {@const key = decisionKey(d)}
  {@const stage = $stages[d.client] || ''}
  {@const open = !!expandedProactive[pd.id]}
  {@const sevClass = d.type === 'going_cold' ? 'sev-blue'
                     : d.type === 'prospecting' ? 'sev-white'
                     : `sev-${d.severity}`}
  {@const subtitleLabel = d.type === 'going_cold' ? 'Gone cold'
                          : d.type === 'prospecting' ? 'Prospecting draft'
                          : d.type === 'meeting_followup' ? 'Meeting follow-up'
                          : 'Overdue follow-up'}
  {@const row3 = d.type === 'going_cold' && d.follow_up
      ? `Gone cold on ${dateWithAgo(d.follow_up, d.age_days)}`
      : d.type === 'prospecting'
      ? `Cold reach-out to ${pd.to || '(recipient)'}`
      : d.context}
  {#if dismissedTiles.has(key)}
    {@render dismissedBackTile(d.client.replace(/_/g, ' '), key)}
  {:else}
  <div class="ctask-swipe-wrap">
  <article class="draft-card {sevClass}" class:open
           onclick={(e) => {
             // Article-level expand so any click on the head (number, client
             // name, stage pill, chevron, subtitle, subject line) toggles.
             // Skip clicks on action buttons + inputs in the expanded body so
             // Send / Regenerate / Skip don't accidentally collapse the tile.
             // `[role="button"]` covers the .draft-preview tap-to-edit affordance
             // so a tap that opens edit mode doesn't ALSO collapse the tile.
             // `.draft-inbound` covers the context blocks (Past thread toggle +
             // its padding ring, From-your-meeting) — the toggle button has
             // stopPropagation, but a click on the block's padding around it used
             // to fall through here and collapse the whole card ("clicking Past
             // thread shrinks the card").
             if ((e.target as HTMLElement).closest('button, input, textarea, label, select, [contenteditable], [role="button"], .draft-inbound')) return;
             // While the editor is open, a stray click on the card chrome (head,
             // padding, the "Changes made" panel) must NOT collapse the tile —
             // that would discard the in-progress edit. Only the explicit
             // Cancel/Send buttons leave the editor.
             if (editingProactiveId === pd.id) return;
             toggleProactive(pd.id);
           }}>
    <!-- role+tabindex: see criticalCard — the keydown handler was unreachable
         without them, leaving every proactive card mouse-only. -->
    <!-- onclick is load-bearing — see criticalCard above. The article's expand
         handler skips `[role="button"]`, so this header needs its own. -->
    <header class="draft-head" role="button" tabindex="0"
            onclick={() => { if (editingProactiveId === pd.id) return; toggleProactive(pd.id); }}
            onkeydown={(e) => { if (editingProactiveId === pd.id) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleProactive(pd.id); } }}>
      <span class="draft-num">{n}</span>
      <div class="draft-head-body">
        <div class="draft-head-row">
          <span class="draft-client">{d.client.replace(/_/g, ' ')}</span>
          {#if stage}
            <span class="draft-stage-pill" style="color:{stageColor(stage)};border-color:color-mix(in srgb, {stageColor(stage)} 40%, transparent);">{$stageLabel(stage)}</span>
          {/if}
          <span class="draft-chev" class:open>›</span>
        </div>
        <div class="draft-subtitle">{subtitleLabel} <span class="draft-meta-sep">·</span> drafted {relativeTime(pd.generated_at)}</div>
        <div class="draft-subject-line">{row3}</div>
      </div>
      <!-- Always-visible housekeeping actions, mirrors criticalCard.
           Send / Regenerate / Skip / Cancel-edit live in the expanded
           body because they're draft-specific; Snooze and Put-on-Hold
           apply regardless of whether the user opens the draft, so they
           sit in the head row. -->
      <div class="draft-actions inline-right">
        {#if d.type === 'meeting_followup' || d.type === 'prospecting'}
          <!-- Respond + Prospect bucket: ship or dismiss. Snooze/Put-on-Hold
               don't make sense — the underlying signal (an attended meeting
               OR a fresh cold-outreach target) doesn't decay on a timer.
               Dismiss is collapsed-only here; when expanded it moves to the
               footer bottom-right (below the stage branch). -->
          {#if !open}
          <button class="today-mini-btn ghost"
                  onclick={(e) => { e.stopPropagation(); skipProactive(d); }}>Dismiss</button>
          {/if}
        {:else if d.type === 'going_cold'}
          <!-- Re-engage bucket: mirrors the Chase card. Put on Hold takes
               Snooze 7d's slot as the PRIMARY action (a cold client is paused,
               not snoozed on a timer), and a Dismiss ghost matches Chase's —
               collapsed-only here in the head, moving to the footer
               bottom-right when the card is expanded. -->
          <button class="today-mini-btn" onclick={(e) => { e.stopPropagation(); putOnHold(d); }}>Put on Hold</button>
          {#if !open}
          <button class="today-mini-btn ghost" onclick={(e) => { e.stopPropagation(); skipProactive(d); }}>Dismiss</button>
          {/if}
        {:else}
          <!-- Chase bucket (overdue): Snooze the prompt OR state-aware
               Dismiss via skipProactive (marks the draft skipped AND
               stamps a thread-fingerprint dismissal, mirrors Respond's
               Dismiss). -->
          <button class="today-mini-btn" onclick={(e) => { e.stopPropagation(); doDecisionSnooze(d, 7); }}>Snooze 7d</button>
          {#if !open}
          <button class="today-mini-btn ghost" onclick={(e) => { e.stopPropagation(); skipProactive(d); }}>Dismiss</button>
          {/if}
        {/if}
      </div>
    </header>
    {#if open}
      <!-- Context block — the prior email thread, in the SAME slot the Respond
           card's THEY WROTE occupies (above the draft, both stages). Collapsed
           by default; the label + expander read it as "here's what led to this
           chase" (respond-chase-v1: past thread above the draft, not below). -->
      {#if pd.thread_id}
        <div class="draft-inbound draft-thread-context">
          <button type="button" class="draft-thread-toggle"
            onclick={(e) => { e.stopPropagation(); toggleThread(pd.id, pd.thread_id); }}>
            <span class="draft-inbound-label">Past thread</span>
            <span class="draft-thread-chev">{expandedThread[pd.id] ? 'Hide ▴' : 'View thread ▾'}</span>
          </button>
          {#if expandedThread[pd.id]}
            {@const tc = threadCache[pd.id]}
            <div class="thread-preview">
              {#if tc === 'loading'}
                <div class="thread-msg-empty">Loading thread…</div>
              {:else if threadError[pd.id]}
                <div class="thread-msg-empty">
                  Couldn’t load this thread.
                  <button type="button" class="et-retry"
                          onclick={(e) => { e.stopPropagation(); loadThread(pd.id, pd.thread_id); }}>Retry</button>
                </div>
              {:else if !Array.isArray(tc) || tc.length === 0}
                <div class="thread-msg-empty">Thread preview unavailable</div>
              {:else}
                {#each (tc as ThreadMessage[]) as msg}
                  <div class="thread-msg">
                    <div class="thread-msg-head">
                      <span class="thread-msg-dir {msg.direction === 'Inbound' ? 'in' : 'out'}">{msg.direction === 'Inbound' ? 'THEY' : 'YOU'}</span>
                      <span class="thread-msg-sender">{msg.sender}</span>
                      <span class="thread-msg-date">{msg.date}</span>
                    </div>
                    <!-- unwrapHardBreaks first: plainToHtml turns every \n into <br>, which
                         is right for a body WE composed and wrong for one we ingested. -->
                    <div class="thread-msg-body">{@html plainToHtml(unwrapHardBreaks(repairLostApostrophes(msg.body)))}</div>
                  </div>
                {/each}
              {/if}
            </div>
          {/if}
        </div>
      {/if}
      <!-- Meeting context (meeting_followup) — the calendar invite this reply
           follows from, in the SAME slot Respond's THEY WROTE / Chase's Past
           thread occupy (above the draft). Real synced-calendar fields only
           (title, when, who) so the rep knows what they're replying to; the
           call summary / transcript is a separate feature not yet wired, and
           is deliberately NOT surfaced as "coming soon" — this reads as
           finished context, not a stub. -->
      {#if d.type === 'meeting_followup' && (pd.meeting_subject || pd.meeting_start || (pd.meeting_attendees?.length ?? 0) > 0)}
        <div class="draft-inbound draft-meeting-context">
          <span class="draft-inbound-label">From your meeting</span>
          {#if pd.meeting_subject}
            <div class="draft-meeting-title">{pd.meeting_subject}</div>
          {/if}
          {#if pd.meeting_start}
            <div class="draft-meeting-when">{formatMeeting(pd.meeting_start, pd.meeting_end)}</div>
          {/if}
          {#if pd.meeting_attendees?.length}
            <div class="draft-meeting-who">
              You met with {nameList(pd.meeting_attendees)}{#if pd.meeting_team?.length}<span class="draft-meeting-sep"> · </span>{nameList(pd.meeting_team)} from your side{/if}
            </div>
          {/if}
        </div>
      {/if}
      {#if editingProactiveId === pd.id}
        <div class="draft-edit"
             onclick={(e) => e.stopPropagation()}
             onmousedown={(e) => e.stopPropagation()}>
          {#if editingProactiveClientContacts.length > 0}
            <MultiSelect label={`TO — ${$terms.counterparty_title} contacts`} options={editingProactiveClientContacts}
                         bind:selected={editingProactiveToClient} />
          {/if}
          <label class="draft-edit-label" for="pto-{pd.id}">TO — Other (comma-separated)</label>
          <input id="pto-{pd.id}" bind:value={editingProactiveToOther}
                 placeholder="other@company.com" />
          {#if editingProactiveClientContacts.length > 0}
            <MultiSelect label={`CC — ${$terms.counterparty_title} contacts`} options={editingProactiveClientContacts}
                         bind:selected={editingProactiveCcClient} />
          {/if}
          {#if editingProactiveInternalContacts.length > 0}
            <MultiSelect label="CC — Your team" options={editingProactiveInternalContacts}
                         bind:selected={editingProactiveCcInternal} />
          {/if}
          <label class="draft-edit-label" for="pco-{pd.id}">CC — Other (comma-separated)</label>
          <input id="pco-{pd.id}" bind:value={editingProactiveCcOther}
                 placeholder="other@company.com" />
          <!-- A threaded reply keeps its subject: Outlook's createReply discards an edited subject and Gmail only threads a matching one. -->
          <label class="draft-edit-label" for="ps-{pd.id}">Subject{#if pd.thread_id}{' '}(locked to thread){/if}</label>
          <input id="ps-{pd.id}" bind:value={editingProactiveSubject} readonly={!!pd.thread_id} />
          <label class="draft-edit-label">Body</label>
          <EmailBodyEditor bind:value={editingProactiveBody} rows={10} />
          {#if d.type === 'prospecting' && marketingDocs.length > 0}
            <label class="draft-edit-label">Attachments</label>
            <div class="chip-row">
              {#each marketingDocs as doc}
                {@const isSelected = editingProactiveAttachIds.includes(doc.id)}
                {@const isRecommended = editingProactiveRecommended.includes(doc.id)}
                <button type="button"
                        class="chip attach"
                        class:selected={isSelected}
                        title={(doc.title || doc.id) + (isRecommended ? ' (AI recommended)' : '')}
                        onclick={() => toggleProactiveAttach(doc.id)}>
                  {#if isRecommended}<span class="draft-spark">✦</span>{/if}<svg class="clip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>{doc.title || doc.id}
                </button>
              {/each}
            </div>
          {/if}
          <!-- Custom file attachments. Available on every proactive tile type
               (overdue / going_cold / meeting_followup / prospecting) so the
               user can attach a one-off PDF, deck, or spreadsheet without
               leaving the Today tile. Native input is hidden behind a
               styled label so the affordance matches the surrounding
               dark-themed mini-buttons. -->
          <label class="draft-edit-label">Custom files</label>
          <div class="proactive-custom-files">
            {#if editingProactiveCustomFiles.length > 0}
              <div class="chip-row" style="margin-bottom:6px;">
                {#each editingProactiveCustomFiles as f, fi}
                  <span class="chip selected"
                        style="display:inline-flex; align-items:center; gap:6px;">
                    {f.name}
                    <span role="button" tabindex="0" style="cursor:pointer; line-height:1;"
                          onclick={() => {
                            editingProactiveCustomFiles = editingProactiveCustomFiles.filter((_, i) => i !== fi);
                          }}
                          onkeydown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              editingProactiveCustomFiles = editingProactiveCustomFiles.filter((_, i) => i !== fi);
                            }
                          }}>&times;</span>
                  </span>
                {/each}
              </div>
            {/if}
            <label class="today-mini-btn file-picker-btn">
              <span>+ Attach file</span>
              <input type="file" multiple class="visually-hidden-input"
                     onchange={(e) => {
                       const input = e.target as HTMLInputElement;
                       if (input.files) {
                         editingProactiveCustomFiles = [
                           ...editingProactiveCustomFiles,
                           ...Array.from(input.files),
                         ];
                       }
                       input.value = '';
                     }} />
            </label>
          </div>
        </div>
      {:else}
        <div class="draft-subject">{pd.subject}</div>
        <div class="draft-badge"><span class="draft-badge-tick">✦</span> DRAFT</div>
        <div class="draft-preview" role="button" tabindex="0"
             title="Click to edit"
             onclick={(e) => { e.stopPropagation(); startEditProactive(d); }}
             onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); startEditProactive(d); } }}>
          {@html plainToHtml(pd.body)}
        </div>
      {/if}

      <div class="draft-actions">
        <!-- Send is blocked while any in-flight Claude op (Polish/Refine/
             Regenerate, all reuse regeneratingProactiveId as the lock) is
             running for THIS draft. Shipping the unfinished body while
             the new version is still computing would land the wrong text. -->
        <button class="btn-primary"
                disabled={sendingProactiveId === pd.id
                       || regeneratingProactiveId === pd.id}
                onclick={() => sendProactive(d)}>
          {sendingProactiveId === pd.id ? 'Sending…'
           : regeneratingProactiveId === pd.id ? 'Working…'
           : 'Send'}
        </button>
        {#if editingProactiveId === pd.id}
          <!-- Stage 2 (editor): Send · Cancel | ✦ rework · Discuss →. divbar
               splits the commit pair from the AI-rework group; ✦ marks each
               rework as George-authored (§5). -->
          <button class="today-mini-btn" onclick={cancelEditProactive}>Cancel</button>
          <span class="divbar"></span>
          <button class="today-mini-btn draft-ai-btn" disabled={regeneratingProactiveId === pd.id}
                  onclick={() => polishOrRefineProactive(d, 'refine')}>
            <span class="draft-spark">✦</span>{regeneratingProactiveId === pd.id ? '…' : 'Refine content'}
          </button>
          <button class="today-mini-btn draft-ai-btn" disabled={regeneratingProactiveId === pd.id}
                  onclick={() => polishOrRefineProactive(d, 'polish')}>
            <span class="draft-spark">✦</span>{regeneratingProactiveId === pd.id ? '…' : 'Polish wording'}
          </button>
          <button class="today-mini-btn draft-ai-btn" disabled={regeneratingProactiveId === pd.id}
                  onclick={() => regenerateProactive(d)}>
            <span class="draft-spark">✦</span>{regeneratingProactiveId === pd.id ? '…' : 'Regenerate'}
          </button>
          <button class="today-mini-btn draft-discuss-btn" onclick={() => discussProactive(d)}>
            Discuss →
          </button>
        {:else}
          <!-- Stage 1 (preview): Send · Edit | Discuss →. Snooze 7d / Put on Hold
               stay in the head; Dismiss moves to the footer bottom-right when
               expanded (added just below), collapsed-only in the head. -->
          <button class="today-mini-btn" onclick={() => startEditProactive(d)}>Edit</button>
          <span class="divbar"></span>
          <button class="today-mini-btn draft-discuss-btn" onclick={() => discussProactive(d)}>
            Discuss →
          </button>
        {/if}
        <!-- Dismiss pinned bottom-right of the EXPANDED footer (respond-chase-v1
             "one Dismiss … bottom-right"). The head shows it collapsed-only; the
             `.draft-actions:not(.inline-right) > .today-mini-btn.ghost` rule
             pushes it to the right edge. going_cold shows it too now (mirrors
             Chase) — Put on Hold is the primary, Dismiss the secondary exit. -->
        <button class="today-mini-btn ghost" onclick={() => skipProactive(d)}>Dismiss</button>
      </div>
      {#if editingProactiveId === pd.id && polishChanges}
        <div class="draft-polish-changes">
          <div class="draft-polish-changes-title">Changes made</div>
          <pre>{polishChanges}</pre>
        </div>
      {/if}
    {/if}
  </article>
  </div>
  {/if}
{/snippet}

<!-- ─── 0. Topbar — Sync control ─── -->
<div class="today-topbar">
  <SyncButton />
</div>
{#if unavailableSections.length || calendarUnavailable}
  <div class="today-empty" role="status">
    Some Today information is temporarily unavailable.
    <button class="today-mini-btn" onclick={() => { loadAll(); loadCalendar(); }}>Retry</button>
  </div>
{/if}

<!-- ─── 1. George header ─── -->
<TodayHero {firstName} agentName={$agentName || 'Dealton'} status={georgeStatus} />

<!-- Sticky count-pill jump nav -->
<TodayNav active={spy.active} onJump={jumpToSection} items={[
  { id: 'today-brief', label: 'Briefing', count: georgeMessages.length },
  { id: 'today-calls', label: 'Calls', count: calls.length },
  { id: 'today-respond', label: 'Respond', count: respondCount },
  { id: 'today-critical', label: 'Chase', count: criticalDecisions.length },
  { id: 'today-prospect', label: 'New business', count: prospectingDrafts.length },
  { id: 'today-reengage', label: 'Gone cold', count: reengageDecisions.length },
]} />

<!-- ─── The Briefing — George's notes ─── -->
<!-- The section renders even when empty. The pill-nav above always offers "Briefing",
     so hiding the whole section left that pill pointing at nothing — and `use:spyOn`
     never attached, so it could never highlight either. Empty state, same as Calls /
     Respond / Chase. -->
<section class="today-section" id="today-brief" use:spyOn>
  <header class="today-section-header">
    <h2>Briefing</h2>
    {#if georgeMessages.length}<span class="today-section-count">{georgeMessages.length} item{georgeMessages.length === 1 ? '' : 's'}{#if briefUnread > 0}{' '}· <span class="today-count-new">{briefUnread} new</span>{/if}</span>{/if}
    <div class="today-section-spacer"></div>
  </header>
  {#if georgeMessages.length > 0}
    {#each (briefsExpanded ? georgeMessages : georgeMessages.slice(0, 5)) as msg, i}
      {@render georgeCard(msg, i + 1)}
    {/each}
    {#if georgeMessages.length > 5}
      <button class="today-mini-btn ghost today-expand-btn" onclick={() => (briefsExpanded = !briefsExpanded)}>
        {briefsExpanded ? 'Show fewer ↑' : `Show ${georgeMessages.length - 5} more brief${georgeMessages.length - 5 === 1 ? '' : 's'} ↓`}
      </button>
    {/if}
  {:else if initialLoadDone && !sectionUnavailable('george_messages')}
    <div class="today-empty">No notes today. When I spot something worth your attention, it lands here.</div>
  {/if}
</section>

<!-- ─── Going out soon — peek at the next pending scheduled sends.
     Capped at 5 in loadScheduled; clicking the header or a row routes
     to Scheduled for the full queue. Sits between Briefing and Calls
     so the user sees what's about to leave their mailbox before they
     triage the day. -->
{#if upcomingSends.length > 0}
  <section class="today-section" id="today-sends">
    <header class="today-section-header sends-header" role="button" tabindex="0"
            onclick={() => ($currentView = 'scheduled')}
            onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); $currentView = 'scheduled'; } }}>
      <h2>Going out soon</h2>
      <span class="today-section-count">{upcomingTotal} pending</span>
    </header>
    <table class="sends-table">
      <tbody>
        {#each upcomingSends as s}
          <tr class="sends-row" role="button" tabindex="0"
              onclick={() => ($currentView = 'scheduled')}
              onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); $currentView = 'scheduled'; } }}>
            <td class="sends-time">{formatCallTime(s.send_at + (s.send_at.endsWith('Z') ? '' : 'Z'))}</td>
            <td class="sends-to">{s.to}</td>
            <td class="sends-subject">{s.subject}</td>
            <td class="sends-actions">
              <button class="today-mini-btn ghost"
                      onclick={(e) => { e.stopPropagation(); cancelSend(s.id); }}>Cancel</button>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </section>
{/if}

<!-- ─── Calls today (moved up — calendar context before the action pile) ─── -->
<section class="today-section" id="today-calls" use:spyOn>
  <header class="today-section-header">
    <h2>Calls today</h2>
    <span class="today-section-count">{calls.length} scheduled</span>
    <div class="today-section-spacer"></div>
  </header>
  {#if calls.length > 0}
    {#each visibleCalls as ev}
      <article class="call-card">
        <div class="call-time">{formatCallTime(ev.start)}</div>
        <div class="call-body">
          <div class="call-title">{ev.summary}</div>
          <div class="call-meta">{ev.attendees.slice(0, 3).join(', ')}{ev.attendees.length > 3 ? ` +${ev.attendees.length - 3}` : ''}</div>
        </div>
        <!-- HIDDEN until the George-invite-to-call flow is complete — for now
             the pre-call brief download is the only live action. To restore,
             change `{#if false}` → `{#if true}`. -->
        {#if false}
        <button
          class="today-mini-btn"
          class:on={ev.george_invited}
          disabled={!ev.has_external && !ev.has_meeting_url}
          onclick={() => toggleCallInvite(ev.id, ev.george_invited)}
        >{ev.george_invited ? '★ ' : '+ '}{($agentName || 'Dealton').split(' ')[0]}</button>
        {/if}
        {#if ev.matched_client}
          <!-- The one live call action: download the pre-call brief PDF.
               Feather "download" icon (tray + down-arrow) + explicit label. -->
          <button
            class="today-mini-btn call-dl-btn"
            disabled={downloadingBrief === ev.id}
            title="Download pre-call brief (PDF)"
            onclick={() => downloadBrief(ev.id)}
          >{#if downloadingBrief === ev.id}…{:else}<svg class="call-dl-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>Download brief{/if}</button>
          <!-- HIDDEN until the client-prep flow is complete. To restore,
               change `{#if false}` → `{#if true}`. -->
          {#if false}
          <button
            class="call-brief-link"
            title="Prepare for {ev.matched_client?.replace(/_/g, ' ')}"
            onclick={() => { if (ev.matched_client) $currentView = ev.matched_client; }}
          >Prepare more →</button>
          {/if}
        {/if}
      </article>
    {/each}
    {#if calls.length > 3}
      <button class="today-mini-btn ghost today-expand-btn" onclick={() => (callsExpanded = !callsExpanded)}>
        {callsExpanded ? 'Show fewer ↑' : `Show ${calls.length - 3} more call${calls.length - 3 === 1 ? '' : 's'} ↓`}
      </button>
    {/if}
  {:else if !calendarUnavailable}
    <div class="today-empty">No calls on the calendar today.</div>
  {/if}
</section>


<!-- ═══ ① RESPOND ═══ -->
<section class="today-section" id="today-respond" use:spyOn>
  <header class="today-section-header">
    <h2>Respond</h2>
    {#if respondCount}<span class="today-section-count">{respondCount} draft{respondCount === 1 ? '' : 's'}</span>{/if}
    <div class="today-section-spacer"></div>
  </header>
  {#each respondFollowups as d, i (decisionKey(d))}
    {@render proactiveDraftCard(d, i + 1)}
  {/each}
  {#if drafts.length > 0}
    {@const inboundStartIdx = respondFollowups.length}
    <!-- Keyed on d.id: this list is replaced wholesale by the 3s poll refresh
         during a sync. Unkeyed, Svelte reuses DOM nodes by index, so a removal
         or reorder mid-edit handed the open editor (keyed by draft id) to a
         different draft's tile. -->
    {#each drafts as d, i (d.id)}
      {#if dismissedTiles.has(d.id)}
        {@render dismissedBackTile(d.client.replace(/_/g, ' '), d.id)}
      {:else}
      {@const stage = $stages[d.client] || ''}
      {@const senderName = d.from_name || d.from_addr.split('@')[0]}
      {@const open = !!expandedDraft[d.id]}
      <div class="ctask-swipe-wrap">
      <article class="draft-card" class:open>
        <!-- Header: click anywhere to toggle expand/collapse. Chevron mirrors
             the Briefing tile's affordance. -->
        <!-- click-to-collapse is suppressed while THIS draft's editor is open so
             a stray header click doesn't discard an in-progress edit (only
             Cancel/Send leave the editor). -->
        <header class="draft-head" role="button" tabindex="0"
                onclick={() => { if (editingDraftId === d.id) return; toggleDraft(d.id); }}
                onkeydown={(e) => { if (editingDraftId === d.id) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleDraft(d.id); } }}>
          <span class="draft-num">{inboundStartIdx + i + 1}</span>
          <div class="draft-head-body">
            <div class="draft-head-row">
              <span class="draft-client">{d.client.replace(/_/g, ' ')}</span>
              {#if stage}
                <span class="draft-stage-pill" style="color:{stageColor(stage)};border-color:color-mix(in srgb, {stageColor(stage)} 40%, transparent);">{$stageLabel(stage)}</span>
              {/if}
              <span class="draft-chev" class:open>›</span>
            </div>
            <!-- 3-line collapsed layout. Same shape proactive (overdue /
                 going_cold) tiles use so all three buckets read as one
                 visual family. Row 3 is "Received {date} ({Nd} ago)" —
                 the inbound's arrival date. "Drafted Nh ago" tucks into
                 row 2 so it doesn't compete for visual space in row 1. -->
            <div class="draft-subtitle">{senderName} emailed · respond now <span class="draft-meta-sep">·</span> drafted {relativeTime(d.generated_at)}</div>
            <div class="draft-subject-line">Received {dateWithAgo(d.inbound_date)}</div>
          </div>
          <!-- Collapsed-only Dismiss — respond-chase-v1 wants ONE Dismiss: on
               the row while collapsed, moving to the footer bottom-right when
               expanded. Hiding it here on open avoids the double-Dismiss. -->
          {#if !open}
          <div class="draft-actions inline-right">
            <button class="today-mini-btn ghost"
                    onclick={(e) => { e.stopPropagation(); skipDraft(d); }}>Dismiss</button>
          </div>
          {/if}
        </header>
        {#if open}
        {#if d.inbound_snippet}
          <!-- Undo the sender's ~76-col plaintext wrap before measuring OR rendering:
               inboundIsLong() counts newlines, so it has to see the same text the
               4-line clamp will. See lib/markdown.ts. -->
          {@const inboundText = unwrapHardBreaks(repairLostApostrophes(d.inbound_snippet))}
          {@const longInbound = inboundIsLong(inboundText)}
          {@const inboundOpen = !!expandedInbound[d.id]}
          <div class="draft-inbound">
            <span class="draft-inbound-label">THEY WROTE</span>
            <span class="draft-inbound-text" class:clamped={longInbound && !inboundOpen}>{inboundText}</span>
            {#if longInbound}
              <button type="button" class="draft-inbound-more"
                      onclick={(e) => { e.stopPropagation(); expandedInbound[d.id] = !inboundOpen; }}>
                {inboundOpen ? 'Show less ▴' : 'Show more ▾'}
              </button>
            {/if}
          </div>
        {/if}
        {#if editingDraftId === d.id}
          <div class="draft-edit"
               onclick={(e) => e.stopPropagation()}
               onmousedown={(e) => e.stopPropagation()}>
            {#if editingClientContacts.length > 0}
              <MultiSelect label={`TO — ${$terms.counterparty_title} contacts`} options={editingClientContacts}
                           bind:selected={editingDraftToClient} />
            {/if}
            <label class="draft-edit-label" for="dto-{d.id}">TO — Other (comma-separated)</label>
            <input id="dto-{d.id}" bind:value={editingDraftToOther}
                   placeholder="other@company.com" />
            {#if editingClientContacts.length > 0}
              <MultiSelect label={`CC — ${$terms.counterparty_title} contacts`} options={editingClientContacts}
                           bind:selected={editingDraftCcClient} />
            {/if}
            {#if editingInternalContacts.length > 0}
              <MultiSelect label="CC — Your team" options={editingInternalContacts}
                           bind:selected={editingDraftCcInternal} />
            {/if}
            <label class="draft-edit-label" for="dco-{d.id}">CC — Other (comma-separated)</label>
            <input id="dco-{d.id}" bind:value={editingDraftCcOther}
                   placeholder="other@company.com" />
            <!-- A threaded reply keeps its subject: Outlook's createReply discards an edited subject and Gmail only threads a matching one. -->
            <label class="draft-edit-label" for="ds-{d.id}">Subject{#if d.thread_id}{' '}(locked to thread){/if}</label>
            <input id="ds-{d.id}" bind:value={editingDraftSubject} readonly={!!d.thread_id} />
            <label class="draft-edit-label">Body</label>
            <EmailBodyEditor bind:value={editingDraftBody} rows={10} />
            <!-- Custom file attachments — same affordance as the proactive
                 tile editor. One-off uploads (PDF, deck, spreadsheet)
                 ride on the send via multipart to /inbox/awaiting/.../send.
                 Styled label hides the native input so the look matches the
                 surrounding mini-button row. -->
            <label class="draft-edit-label">Attachments</label>
            <div class="proactive-custom-files">
              {#if editingDraftCustomFiles.length > 0}
                <div class="chip-row" style="margin-bottom:6px;">
                  {#each editingDraftCustomFiles as f, fi}
                    <span class="chip selected"
                          style="display:inline-flex; align-items:center; gap:6px;">
                      {f.name}
                      <span role="button" tabindex="0" style="cursor:pointer; line-height:1;"
                            onclick={() => {
                              editingDraftCustomFiles = editingDraftCustomFiles.filter((_, i) => i !== fi);
                            }}
                            onkeydown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                editingDraftCustomFiles = editingDraftCustomFiles.filter((_, i) => i !== fi);
                              }
                            }}>&times;</span>
                    </span>
                  {/each}
                </div>
              {/if}
              <label class="today-mini-btn file-picker-btn">
                <span>+ Attach file</span>
                <input type="file" multiple class="visually-hidden-input"
                       onchange={(e) => {
                         const input = e.target as HTMLInputElement;
                         if (input.files) {
                           editingDraftCustomFiles = [
                             ...editingDraftCustomFiles,
                             ...Array.from(input.files),
                           ];
                         }
                         input.value = '';
                       }} />
              </label>
            </div>
            {#if polishChanges}
              <div class="draft-polish-changes">
                <div class="draft-polish-changes-title">Changes made</div>
                <pre>{polishChanges}</pre>
              </div>
            {/if}
          </div>
        {:else}
          <div class="draft-subject">{replySubjectFor(d)}</div>
          <div class="draft-badge"><span class="draft-badge-tick">✦</span> DRAFT</div>
          <!-- Clickable body preview — the drafted reply IS the artifact the
               user wants to interact with, so the preview itself opens the
               editor. Subtle hover hint makes the affordance discoverable. -->
          <div class="draft-preview" role="button" tabindex="0"
               title="Click to edit"
               onclick={() => startEditDraft(d)}
               onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); startEditDraft(d); } }}>
            {@html plainToHtml(d.draft_body)}
          </div>
        {/if}
        <div class="draft-actions">
          <!-- Send is blocked while Polish/Refine/Regenerate is in flight
               for THIS draft (all reuse polishingDraftId as the lock). -->
          <button class="btn-primary"
                  disabled={sendingDraftId === d.id
                         || polishingDraftId === d.id}
                  onclick={() => sendDraft(d)}>
            {sendingDraftId === d.id ? 'Sending…'
             : polishingDraftId === d.id ? 'Working…'
             : 'Send'}
          </button>
          {#if editingDraftId === d.id}
            <!-- Stage 2 (editor): Send · Cancel | ✦ rework · Discuss → … Dismiss.
                 divbar splits the commit pair from the AI-rework group; the ✦
                 marks each rework as George-authored (§5). -->
            <button class="today-mini-btn" onclick={cancelEditDraft}>Cancel</button>
            <span class="divbar"></span>
            <button class="today-mini-btn draft-ai-btn" disabled={polishingDraftId === d.id}
                    onclick={() => polishOrRefine(d, 'refine')}>
              <span class="draft-spark">✦</span>{polishingDraftId === d.id ? '…' : 'Refine content'}
            </button>
            <button class="today-mini-btn draft-ai-btn" disabled={polishingDraftId === d.id}
                    onclick={() => polishOrRefine(d, 'polish')}>
              <span class="draft-spark">✦</span>{polishingDraftId === d.id ? '…' : 'Polish wording'}
            </button>
            <button class="today-mini-btn draft-ai-btn" disabled={polishingDraftId === d.id}
                    onclick={() => regenerateDraft(d)}>
              <span class="draft-spark">✦</span>{polishingDraftId === d.id ? '…' : 'Regenerate'}
            </button>
            <button class="today-mini-btn draft-discuss-btn" onclick={() => discussDraft(d)}>
              Discuss →
            </button>
            <button class="today-mini-btn ghost" onclick={() => skipDraft(d)}>Dismiss</button>
          {:else}
            <!-- Stage 1 (preview): Send · Edit | Discuss → … Dismiss.
                 Edit opens the editor (the preview body is also click-to-edit). -->
            <button class="today-mini-btn" onclick={() => startEditDraft(d)}>Edit</button>
            <span class="divbar"></span>
            <button class="today-mini-btn draft-discuss-btn" onclick={() => discussDraft(d)}>
              Discuss →
            </button>
            <button class="today-mini-btn ghost" onclick={() => skipDraft(d)}>Dismiss</button>
          {/if}
        </div>
        {/if}
      </article>
      </div>
      {/if}
    {/each}
  {:else if initialLoadDone && !sectionUnavailable('drafts') && !sectionUnavailable('decisions') && meetingFollowupDecisions.length === 0}
    <!-- Empty state only when the WHOLE Respond bucket is empty — not just when
         there are no inbound drafts. Meeting-followups render above and count
         toward respondCount, so keying on drafts.length alone showed both the
         cards AND the "inbox answered" placeholder at once. -->
    <div class="today-empty">Your inbox is answered. No one's waiting on you right now — nice work.</div>
  {/if}
</section>

<!-- ═══ ② CHASE ═══ -->
<section class="today-section" id="today-critical" use:spyOn>
  <header class="today-section-header">
    <h2>Chase</h2>
    {#if criticalDecisions.length}<span class="today-section-count">{criticalDecisions.length} flag{criticalDecisions.length === 1 ? '' : 's'}</span>{/if}
    <div class="today-section-spacer"></div>
  </header>
  {#if criticalDecisions.length > 0}
    {#each visibleCritical as d, i (decisionKey(d))}
      {#if d.proactive_draft && (d.type === 'overdue' || d.type === 'going_cold')}
        {@render proactiveDraftCard(d, i + 1)}
      {:else}
        {@render criticalCard(d, i + 1)}
      {/if}
    {/each}
    {#if criticalDecisions.length > 5}
      <button class="today-mini-btn ghost today-expand-btn"
              onclick={() => criticalExpanded = !criticalExpanded}>
        {criticalExpanded
          ? 'Show fewer ↑'
          : `Show ${criticalDecisions.length - 5} more ↓`}
      </button>
    {/if}
  {:else if initialLoadDone && !sectionUnavailable('decisions')}
    <div class="today-empty">Nothing on fire. I'm watching every account — if something slips, you'll see it here first.</div>
  {/if}
</section>

<!-- reactive → proactive transition -->
<div class="george-transition"><span>{transitionLine}</span></div>

<!-- ═══ ③ NEW BUSINESS ═══ -->
<section class="today-section" id="today-prospect" use:spyOn>
  <header class="today-section-header">
    <h2>New business</h2>
    {#if hunt}<span class="today-section-count">{hunt.eligible_total} in play · {#if hunt.streak.days > 0}<span class="today-count-streak">🔥 {hunt.streak.days}-day streak</span>{:else if hunt.streak.this_week === 0}none reached out yet{:else}{hunt.streak.this_week} reached out this week{/if}</span>{/if}
    <div class="today-section-spacer"></div>
  </header>

  {#each prospectCards as d, i (decisionKey(d))}
    {@render criticalCard(d, i + 1)}
  {/each}

  <!-- Prospecting drafts — Respond-style tiles with white accent + attachment
       editor. Backend tops up to 3/day and clears at 10 days stale. -->
  <!-- Numbering continues from the prospectCards loop above — both render into
       the same section, so restarting at 1 gave the section two "1" tiles. -->
  {#each prospectingDrafts as d, i (decisionKey(d))}
    {@render proactiveDraftCard(d, prospectCards.length + i + 1)}
  {/each}

  {#if hunt}
    {#if prospectingDrafts.length === 0}
      <div class="today-empty">Today's prospecting is done. Three fresh cold reach-outs every morning — keep the streak alive.</div>
    {/if}

    <!-- Prospecting track record -->
    {#if hunt.scoreboard.series.length > 0}
      {@const sb = hunt.scoreboard}
      <div class="hunt-scoreboard">
        <div class="hunt-scoreboard-head">
          <span class="hunt-scoreboard-title">Prospecting Track Record</span>
          <span class="hunt-scoreboard-sub">first replies, last 6 months</span>
        </div>
        <TrackRecordChart
          xLabels={sb.months}
          series={sb.series.map((s, si) => ({
            label: s.display_name,
            color: HUNT_LINE_COLORS[si % HUNT_LINE_COLORS.length],
            points: s.counts,
          }))}
        />
        <div class="hunt-chart-legend">
          {#each sb.series as s, si}
            <span class="hunt-legend-item">
              <span class="hunt-legend-dot" style="background:{HUNT_LINE_COLORS[si % HUNT_LINE_COLORS.length]};"></span>
              {s.display_name}
              <span class="hunt-legend-total">{s.counts.reduce((a, b) => a + b, 0)}</span>
            </span>
          {/each}
        </div>
      </div>
    {/if}
    <!-- → = navigate (to the full Prospecting page), distinct from the ↓ expand
         toggles. Coral text-link per the design system's nav-verb rule. -->
    <button class="today-section-link" onclick={() => ($currentView = 'prospecting')}>Open Prospecting →</button>
  {:else}
    <div class="today-empty">Lining up today's targets…</div>
  {/if}
</section>

<!-- ═══ ④ GONE COLD ═══ -->
<section class="today-section" id="today-reengage" use:spyOn>
  <header class="today-section-header">
    <h2>Gone cold</h2>
    {#if reengageDecisions.length}<span class="today-section-count">{reengageDecisions.length} cold</span>{/if}
    <div class="today-section-spacer"></div>
  </header>
  {#if reengageDecisions.length > 0}
    {#each visibleCold as d, i (decisionKey(d))}
      {#if d.proactive_draft}
        {@render proactiveDraftCard(d, i + 1)}
      {:else}
        {@render reengageCard(d, i + 1)}
      {/if}
    {/each}
    {#if reengageDecisions.length > 3}
      <button class="today-mini-btn ghost today-expand-btn" onclick={() => (coldExpanded = !coldExpanded)}>
        {coldExpanded ? 'Show fewer ↑' : `Show ${reengageDecisions.length - 3} more ↓`}
      </button>
    {/if}
  {:else if initialLoadDone && !sectionUnavailable('decisions')}
    <div class="today-empty">No one's gone cold. Every client's heard from you recently — that's a healthy book.</div>
  {/if}
</section>

<style>
  /* ─── Topbar (Sync control) ─── */
  .today-topbar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: 14px;
  }

  /* ─── George header ─── */
  /* ─── Section frame ─── */
  .today-section { margin-bottom: 28px; scroll-margin-top: 56px; }
  .today-section-header {
    display: flex;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 12px;
    padding-bottom: 6px;
    border-bottom: 1px solid var(--border);
  }
  .today-section-header h2 {
    /* App sans (inherits body) — page/section titles are sans app-wide; only the
       .today-hero "Hi …" greeting keeps the Fraunces serif. See DECISIONS 2026-07-20. */
    font-family: inherit;
    font-size: 15px;
    font-weight: 500;
    letter-spacing: -0.01em;
    color: var(--text);
  }
  .today-section-count {
    font-size: 11px;
    color: var(--text-dim);
    font-family: var(--font-mono);
  }
  /* Streak inline in the New-business count line — a touch brighter than the
     dim count so it reads as a small "win" without shouting. */
  .today-count-streak { color: var(--text-muted); }
  /* Unread "N new" in the Briefing count line — coral (George's attention mark,
     matches the coral unread dot) so the eye catches it. */
  .today-count-new { color: var(--accent-ink); }
  /* Section-level nav link (e.g. "Open Prospecting →") — same muted treatment as
     the "Show N more" expand buttons; the → (vs ↓) is what marks it as navigate. */
  .today-section-link {
    display: block;
    margin-top: 8px;
    background: none;
    border: none;
    padding: 2px 0;
    /* Match .today-expand-btn exactly — NOT `font: inherit`, whose shorthand
       resets font-weight to the body's 400 and made this read lighter than
       the "Show N more" button sitting right beside it (buttons default to
       font-weight:500 via the global button rule). */
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    color: var(--text-dim);
    cursor: pointer;
    text-align: left;
  }
  .today-section-link:hover { color: var(--text-muted); }
  .today-section-spacer { flex-grow: 1; }

  /* .today-mini-btn base / :hover / .ghost / :disabled moved to app.css (shared
     draft-editor vocabulary — used by both Today and ClientView). Only the
     Today-specific .on toggle state stays here, layering on the global base. */
  .today-mini-btn.on {
    background: color-mix(in srgb, var(--accent) 14%, transparent);
    color: var(--accent-ink);
    border-color: color-mix(in srgb, var(--accent) 40%, transparent);
  }
  /* "Show N more / fewer" — a plain LEFT-aligned text button across every
     section (was centered for Briefing/Calls/Gone-cold via margin:auto and a
     bordered pill for Chase). No border, ever — not even on hover — so it reads
     as a quiet text affordance, not a control competing with the tiles. */
  .today-expand-btn {
    display: block;
    margin: 8px 0 0;
    padding: 2px 0;
    background: transparent;
    border: none;
    color: var(--text-dim);
    text-align: left;
  }
  .today-expand-btn:hover {
    background: transparent;
    border: none;
    color: var(--text-muted);
  }

  /* Dismissed back-of-tile — replaces the full tile content when the
     user dismisses. Same outer footprint as the original (matches the
     vertical rhythm of decision/critical/brief/reengage tiles) so the
     surrounding list doesn't jump on dismiss. Subdued + slightly
     dashed border to communicate "this is gone but recoverable". */
  .dismissed-back-tile {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border: 1px dashed var(--border);
    border-radius: 8px;
    background: color-mix(in srgb, var(--bg-surface) 60%, transparent);
    color: var(--text-dim);
    margin: 4px 0;
    transition: opacity 0.15s;
  }
  .dismissed-back-tile:hover { opacity: 1; border-color: var(--border-strong); }
  .dismissed-back-text {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    flex: 1;
  }
  .dismissed-back-label {
    font-weight: 600;
    color: var(--text-muted);
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .dismissed-back-status {
    font-size: 11px;
    font-style: italic;
    color: var(--text-dim);
    opacity: 0.7;
  }
  .dismissed-back-status::before {
    content: "·";
    margin-right: 10px;
    opacity: 0.5;
  }
  .dismissed-undo-btn { flex-shrink: 0; }

  /* Styled file picker — hides the native input (which renders an OS-themed
     "Choose Files" button that clashes with the dark UI) behind a label
     that inherits .today-mini-btn styling. Inline-flex so the label sits
     on its own row inside the attachment widget, not stretched full-width
     like normal labels in .draft-edit. */
  .file-picker-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    width: fit-content;
  }
  .visually-hidden-input {
    /* Keep the input in the DOM (so the click on its label-parent opens
       the OS picker) but invisible. position:absolute keeps it from
       contributing layout height to the label. */
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
    overflow: hidden;
  }

  /* .btn-primary base / :hover / :disabled moved to app.css (shared draft-editor
     vocabulary). Today's responsive tweaks to it remain in the media queries below. */

  /* ─── reactive→proactive transition ─── */
  .george-transition {
    display: flex;
    align-items: center;
    gap: 14px;
    margin: 4px 0 24px;
    color: var(--text-dim);
  }
  .george-transition::before,
  .george-transition::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
  }
  .george-transition span {
    font-family: inherit;
    font-style: italic;
    font-size: 12.5px;
    text-align: center;
    white-space: nowrap;
  }

  /* ─── The Briefing — George note tiles ─── */
  .gnote-tile {
    /* Vertically center content (title row + 2-line preview) so short
       briefs don't sit pinned at the top of an oversized tile. The
       min-height gives the stack a uniform "lower bound" — taller
       briefs grow past it naturally. */
    align-items: center;
    min-height: 84px;
    cursor: pointer;
  }
  /* margin-bottom is on the .ctask-swipe-wrap that now wraps each tile */
  .gnote-tile.unread { background: color-mix(in srgb, var(--accent) 4%, var(--bg-surface)); }
  .gnote-namerow {
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    margin-bottom: 2px;
    background: transparent;
    border: none;
    padding: 0;
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;
  }
  .gnote-name {
    font-size: 15px;
    font-weight: 600;
    color: var(--text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  /* Unread marker — a small coral dot, not a "NEW" text pill: a second
     red/coral pill next to the type tag (SLIPPING, COOLING…) read as clutter.
     The dot signals unread without competing with the status pill. */
  .gnote-dot {
    flex-shrink: 0;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--accent);
  }
  .gnote-time {
    margin-left: auto;
    flex-shrink: 0;
    font-size: 10.5px;
    color: var(--text-dim);
    font-family: var(--font-mono);
  }
  .gnote-chev {
    flex-shrink: 0;
    color: var(--text-dim);
    font-size: 15px;
    line-height: 1;
    transition: transform 0.12s;
  }
  .gnote-chev.open { transform: rotate(90deg); }
  .gnote-full {
    font-size: 12.5px;
    line-height: 1.55;
    color: var(--text-muted);
    margin-top: 8px;
    /* Explicitly selectable so users can copy/paste the brief body. */
    user-select: text;
    -webkit-user-select: text;
    cursor: text;
  }
  .gnote-full button { cursor: pointer; }

  /* ─── dry_funnel structured prospect render ─────────────────────────
     The dry_funnel briefing uses the structured payload instead of raw
     markdown so each prospect gets actionable controls: the email is a
     button that jumps to Prospecting pre-seeded, plus a Discuss button
     that opens a chat about that specific prospect. */
  /* Cap the reading width — at wide viewports the brief otherwise runs
     ~150-char lines. ~760px keeps line length readable (today-brief-v20). */
  .dry-funnel-brief { font-size: 12.5px; line-height: 1.55; max-width: 760px; }
  .dry-opener { margin-bottom: 10px; }
  /* Per-company section — contacts + brief stacked, with the "Make first
     contact →" CTA at the BOTTOM-left so it follows the read → act order
     (today-brief-v20). Companies separated by a light divider, not a box. */
  .dry-company {
    border-top: 1px solid var(--border);
    padding: 10px 0;
  }
  .dry-company:last-of-type { padding-bottom: 4px; }
  .dry-company-actions { margin-top: 8px; }
  .dry-prospect {
    border-top: 1px solid var(--border);
    padding: 10px 0;
  }
  .dry-prospect:last-of-type { padding-bottom: 4px; }
  .dry-prospect-co {
    margin: 0 0 4px 0;
    font-size: 13px;
    color: var(--text);
    font-weight: 600;
  }
  .dry-prospect-meta { color: var(--text); margin-bottom: 4px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  /* Email — INFO, not an action: blue per DESIGN-SYSTEM §1 (coral is
     reserved for the clickable "Make first contact →"). Not clickable,
     no underline — so it doesn't compete with the real CTA. */
  .dry-prospect-email { color: var(--blue); }
  /* Inline + icon for "ingest this person as a Prospect-stage contact". */
  .dry-prospect-add-icon {
    width: 20px; height: 20px;
    display: inline-flex; align-items: center; justify-content: center;
    border: 1px solid var(--border-strong);
    border-radius: 4px;
    background: var(--bg-surface);
    color: var(--text);
    font-size: 13px; line-height: 1;
    cursor: pointer;
    padding: 0;
  }
  .dry-prospect-add-icon:hover:not(:disabled) {
    background: var(--bg-hover);
    border-color: var(--text-muted);
  }
  .dry-prospect-add-icon:disabled {
    cursor: default;
    color: var(--success, #16a34a);
    border-color: var(--success, #16a34a);
  }
  /* Company blurb — italic + dimmed so it reads as context, below the
     actionable "How to approach" line. */
  .dry-prospect-fit { margin: 4px 0; color: var(--text-dim); font-style: italic; }
  .dry-prospect-approach { margin: 4px 0; color: var(--text-muted); }
  .dry-prospect-actions { display: flex; gap: 6px; margin-top: 6px; }

  /* ─── Draft (Respond) card — briefing-style aesthetic ─────────────────
     Mirrors the .ctask family used by Critical and Briefing so the visual
     language is consistent across the three Today bucket types. Distinct
     accents per family: Critical=red severity, Briefing=msg_type colour,
     Respond=green (signals "ready to ship"). */
  .draft-card {
    position: relative;
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 15px 18px;
    margin-bottom: 10px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow: hidden;
    transition: border-color 0.12s, background 0.12s;
  }
  .draft-card:hover { border-color: var(--border-strong); }
  /* Background light-up on hover (like the Briefing card) — but ONLY while
     collapsed. A collapsed card is header-only, so nothing blends; once open it
     has `--bg-hover` inner boxes (THEY WROTE / meeting / preview) that would
     vanish into a same-colour card, so open cards get border-only hover. */
  .draft-card:not(.open):hover { background: var(--bg-hover); }
  /* Left accent strip — same shape as .ctask::before. Green = "this is your
     drafted reply, ready to ship." */
  .draft-card::before {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 3px;
    background: var(--green);
  }
  /* Header: numbered prefix + client name + stage pill + time on the right.
     Number sizing, family, and header alignment all mirror .ctask /
     .gnote-namerow so the three Today tile families read as a visual set. */
  .draft-head {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .draft-num {
    flex-shrink: 0;
    width: 22px;
    text-align: center;
    font-family: 'Fraunces', Georgia, serif;
    font-size: 22px;
    font-weight: 500;
    color: var(--text-dim);
    line-height: 1;
  }
  .draft-head-body { flex-grow: 1; min-width: 0; }
  .draft-head-row {
    display: flex;
    align-items: center;
    gap: 9px;
    flex-wrap: wrap;
  }
  .draft-client {
    font-size: 15px;
    font-weight: 600;
    color: var(--text);
  }
  .draft-stage-pill {
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 2px 6px;
    border: 1px solid;
    border-radius: 4px;
    background: transparent;
  }
  .draft-time {
    margin-left: auto;
    font-size: 11px;
    color: var(--text-dim);
    flex-shrink: 0;
  }
  /* `.draft-chev` is global (app.css); the hover rule below layers on top of it. */
  /* Whole header reads as a button while collapsed; the chevron rotation
     and hover background give the affordance. */
  .draft-head {
    cursor: pointer;
    user-select: none;
  }
  .draft-head:hover .draft-chev { color: var(--text-muted); }
  .draft-subtitle {
    margin-top: 2px;
    font-size: 12px;
    color: var(--text-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  /* "· drafted Nh ago" separator in the subtitle. Pure visual breath
     between the contextual label and the freshness meta — dimmer than the
     surrounding text so it reads as a separator, not a content word. */
  .draft-meta-sep { color: var(--text-dim); opacity: 0.6; margin: 0 4px; }
  /* Third collapsed line — the subject line. One step dimmer than the
     subtitle so the visual weight order is: client name > who/why >
     subject. Shared by Respond + Proactive tiles. */
  .draft-subject-line {
    margin-top: 1px;
    font-size: 11.5px;
    color: var(--text-dim);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-style: italic;
  }
  /* Severity-color overrides for the proactive draft tile's left accent
     strip. The base `.draft-card::before` is green (= "your reply is
     ready"); Critical/Re-engage versions inherit the severity hue from
     the decision row so the bucket reads at a glance. */
  .draft-card.sev-red::before    { background: var(--red); }
  .draft-card.sev-amber::before  { background: #F5B042; }
  .draft-card.sev-blue::before   { background: var(--blue, #60a5fa); }
  /* Prospecting tiles — neutral white/grey accent so they read as
     "forward, not urgent" against the urgent Critical/Re-engage tiles. */
  .draft-card.sev-white::before  { background: var(--text-dim, #9ca3af); }
  /* Attachment picker — pill-style toggle row, same look the Prospecting
     page uses. Selected pills fill with --accent; unselected are
     outlined. AI-recommended docs get a ✨ prefix that survives the user
     toggling them off so they can find their way back. */
  /* The attachment pills use the global `.chip` / `.chip-row` (app.css) — the same
     class as the pickers on ClientView and Prospecting and the filter chips on
     Prospecting / MetaTab. This page's values are the standard; change them there. */
  /* Critical fallback tile (no draft): no expandable body, so action
     buttons live INSIDE the head, flushed right. Center-align so the
     number prefix and the right-side buttons sit vertically centered
     against the 3-line head body. */
  .draft-card.critical-fallback .draft-head { align-items: center; }
  /* `.draft-actions.inline-right` is global (app.css); the `:not(.inline-right)` rules
     below are this page's own. */
  /* Nested tiles: the outer .ctask-swipe-wrap already provides the bottom
     margin; zero out the inner .draft-card's margin so the height matches
     the bare `<article>` case. Doubled margins were leaving an awkward
     gap that read as extra padding inside the tile. */
  .ctask-swipe-wrap > .draft-card { margin-bottom: 0; }
  /* Body-preview border stays green across ALL tile severities — the
     green = "your reply is ready to ship" cue is preserved, while the
     OUTER left strip carries the bucket's severity hue. */
  /* "They wrote" bubble — keeps the inbound preview visually distinct from
     the user's drafted reply below. Muted background, no accent so the
     drafted reply (green) stands out as the actionable content. Label sits
     above the text always — clearer visual hierarchy than side-by-side. */
  .draft-inbound {
    background: var(--bg-hover);
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 12.5px;
    color: var(--text-muted);
    line-height: 1.55;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .draft-inbound-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    /* Uppercase so all context-block labels match regardless of how the literal
       text is typed — "THEY WROTE" was caps, "Past thread" / "From your meeting"
       weren't; the 700 weight + 0.08em tracking is an uppercase eyebrow style. */
    text-transform: uppercase;
    color: var(--text-dim);
  }
  .draft-inbound-text {
    /* Preserve newlines from the stripped sender-only message so multi-line
       replies read naturally instead of collapsing to one wall of text.
       The strip helper normalises \r\n→\n upstream so this works the same
       across providers. What survives here is the sender's AUTHORED breaks
       only — unwrapHardBreaks() removes their ~76-col wrap first, or this rule
       would faithfully reproduce a line width that isn't ours. */
    white-space: pre-wrap;
  }
  /* Collapsed: clamp to ~4 lines (what the old 82px scroll box held) and hide
     the overflow. Without it a long inbound scrolls inside a fixed box; instead it
     expands in place via the Show more/less toggle below — no nested scroll
     region competing with the page scroll. */
  .draft-inbound-text.clamped {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 4;
    line-clamp: 4;
    overflow: hidden;
  }
  .draft-inbound-more {
    align-self: flex-start;
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-muted);
  }
  .draft-inbound-more:hover { color: var(--text); }
  /* Meeting context block (meeting_followup) — reuses the .draft-inbound shell
     like the past-thread block, but presents fixed calendar facts (title / when
     / who), so no scroll cap. Title carries slight emphasis; when + who are
     muted supporting lines. Tighter gap than the default shell since these are
     short one-liners, not prose. */
  .draft-meeting-context { gap: 4px; }
  .draft-meeting-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
  }
  .draft-meeting-when,
  .draft-meeting-who {
    font-size: 12px;
    color: var(--text-muted);
  }
  .draft-meeting-sep { color: var(--text-dim); }
  /* Past-thread context block — reuses the .draft-inbound (THEY WROTE) shell
     so the chase's prior-thread reads as the same kind of context slot, sitting
     above the draft. The whole label row is the collapse/expand toggle. */
  .draft-thread-context { cursor: pointer; }
  .draft-thread-toggle {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
  }
  .draft-thread-chev {
    margin-left: auto;
    font-size: 10.5px;
    font-weight: 600;
    color: var(--text-muted);
  }
  .draft-thread-toggle:hover .draft-thread-chev { color: var(--text); }
  .draft-thread-context .thread-preview { margin-top: 8px; }
  .thread-preview {
    background: var(--bg-hover);
    border-radius: 8px;
    padding: 8px 10px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 440px;
    overflow-y: auto;
  }
  .thread-msg { display: flex; flex-direction: column; gap: 3px; }
  .thread-msg + .thread-msg { border-top: 1px solid var(--border); padding-top: 8px; }
  .thread-msg-head { display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap; }
  .thread-msg-dir {
    font-size: 9.5px;
    font-weight: 700;
    letter-spacing: 0.07em;
    padding: 1px 5px;
    border-radius: 4px;
  }
  .thread-msg-dir.in { background: var(--accent, #2563eb); color: #fff; }
  .thread-msg-dir.out { background: var(--border); color: var(--text-muted); }
  .thread-msg-sender { font-size: 11.5px; font-weight: 600; color: var(--text); }
  .thread-msg-date { font-size: 10.5px; color: var(--text-dim); margin-left: auto; }
  .thread-msg-body {
    font-size: 12px;
    color: var(--text-muted);
    line-height: 1.5;
  }
  /* plainToHtml renders its own <br>/lists — keep list indents tidy */
  .thread-msg-body :global(ul),
  .thread-msg-body :global(ol) { margin: 4px 0; padding-left: 20px; }
  .thread-msg-body :global(b) { color: var(--text); }
  .thread-msg-empty { font-size: 12px; color: var(--text-dim); padding: 2px 0; }
  /* Underlined text, not a filled button: it IS an action, but a transient error
     should not read as loudly as Send. Mirrors ClientView's `.et-retry`. */
  .et-retry {
    background: none; border: none; padding: 0; margin-left: 6px;
    font: inherit; color: var(--accent-ink); text-decoration: underline; cursor: pointer;
  }
  .et-retry:hover { color: var(--accent-hover); }

  /* Subject row above the body preview */
  .draft-subject {
    font-size: 14px;
    font-weight: 600;
    color: var(--text);
    margin-top: 2px;
  }
  /* DRAFT badge — ✦ + "DRAFT" both coral (respond-chase-v1 / DESIGN-SYSTEM §5:
     the ✦ is George's authorship signature = coral brand, NOT green. Green is
     reserved strictly for on-track deal status). Four-point sparkle prefix
     reads as "freshly generated by George." */
  .draft-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    color: var(--accent-ink);
    text-transform: uppercase;
    margin-top: -2px;
  }
  .draft-badge-tick {
    font-size: 11px;
    line-height: 1;
  }
  /* Body preview — neutral highlighted surface (respond-chase-v1: "remove the
     green inner left bar — green is the outer card's on-track status only.
     Box = neutral highlighted surface"). Clickable: hover lifts the
     background a touch + shows the cursor pointer; that's the affordance for
     "click to edit." */
  .draft-preview {
    font-size: 12.5px;
    line-height: 1.6;
    color: var(--text);
    background: var(--bg-hover);
    padding: 10px 12px;
    border-radius: 6px;
    border: 1px solid var(--border);
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s;
  }
  .draft-preview:hover {
    border-color: var(--border-strong);
  }
  .draft-preview:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
  /* Tame the markdown-rendered HTML inside the preview so lists/paragraphs
     don't blow the card vertical rhythm out. */
  .draft-preview :global(ul),
  .draft-preview :global(ol) { margin: 4px 0; padding-left: 20px; }
  .draft-preview :global(li) { margin: 0; }
  .draft-edit { display: flex; flex-direction: column; gap: 6px; }
  /* Casing is source-controlled (TO/CC uppercase in markup, everything
     else title case) so labels read as "TO — Other" not "TO — OTHER". */
  .draft-edit-label { font-size: 10.5px; color: var(--text-dim); letter-spacing: 0.06em; }
  /* "Changes made" panel — Polish/Refine/Regenerate notes from Claude.
     Same visual treatment ClientView and Prospecting use so the
     affordance reads the same on every drafting surface. */
  .draft-polish-changes {
    margin-top: 6px;
    padding: 8px 10px;
    background: color-mix(in srgb, var(--accent) 6%, var(--bg-surface));
    border-left: 2px solid var(--accent);
    border-radius: 4px;
  }
  .draft-polish-changes-title {
    font-size: 10.5px;
    font-weight: 700;
    color: var(--text-dim);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 4px;
  }
  .draft-polish-changes pre {
    margin: 0;
    font-family: inherit;
    font-size: 12px;
    color: var(--text);
    white-space: pre-wrap;
    word-break: break-word;
  }
  /* Discuss — underlined text in the action row, no background or border
     fill. Signals "leave this page and continue this elsewhere" without
     competing with the primary Send / Review & edit action shape. Uses
     var(--accent) so the color tracks the Send button exactly across
     themes (orange-red in dark/light, blue in default). */
  .draft-discuss-btn {
    background: transparent !important;
    /* transparent (not `none`) so it reserves the same 1px border box as the
       bordered buttons — keeps Discuss the same height + baseline as Send/Edit. */
    border: 1px solid transparent !important;
    box-shadow: none !important;
    padding-left: 4px;
    padding-right: 4px;
    color: var(--accent-ink);
    /* Nudge down a couple px — the borderless text-link sat slightly high
       against the bordered Send/Edit buttons on the same baseline. transform
       (not margin) so it doesn't reflow the flex row's height. */
    transform: translateY(2px);
  }
  /* Underline on hover only — matches the briefing "Re-engage →" verb (.ctask-btn),
     not a permanently-underlined link. */
  .draft-discuss-btn:hover {
    background: transparent !important;
    color: var(--accent-hover);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  .draft-edit input {
    width: 100%;
    font-family: inherit;
    font-size: 13px;
    padding: 8px 10px;
    border: 1px solid var(--border);
    border-radius: 5px;
    background: var(--bg);
    color: var(--text);
  }
  .draft-edit input:focus { outline: none; border-color: var(--border-strong); }
  /* Expanded-body action row — Send / Cancel / Polish / Refine / Regenerate
     / Skip / Discuss. Wraps onto multiple lines if the viewport can't fit
     the row in one (tight tablets, mid-size phones). The progressive
     mobile rule below tightens font/padding further so 6+ buttons stay
     legible at narrow widths. */
  .draft-actions {
    display: flex;
    gap: 6px;
    align-items: center;
    flex-wrap: wrap;
    row-gap: 6px;
  }
  /* Vertical hairline separating the commit group (Send · Edit / Send · Cancel)
     from the nav/rework group — respond-chase-v1 "Send · Edit | Discuss →". */
  .divbar {
    width: 1px;
    height: 20px;
    background: var(--border);
    margin: 0 2px;
    align-self: center;
    flex-shrink: 0;
  }
  /* ✦ AI-authorship mark on the rework buttons (Refine / Polish / Regenerate),
     coral per DESIGN-SYSTEM §5. Quiet — sits inside the existing mini-button. */
  /* .draft-spark moved to app.css (shared draft-editor vocabulary). */
  /* ✦ smart-edit buttons (Refine content / Polish wording / Regenerate) —
     borderless per respond-chase-v1: the coral ✦ already marks them as
     George's edits, so they read as a light tool group, not another boxed
     button competing with Cancel. Background appears only on hover. Scoped
     under .draft-actions (0,2,0) to beat the base .today-mini-btn border/
     background (0,1,0) by specificity — no !important needed. */
  .draft-actions .draft-ai-btn {
    background: transparent;
    border-color: transparent;
    /* Lighter than the 500 the global button rule gives — these ✦ tools read
       as a quiet group, not primary controls. */
    font-weight: 400;
    /* Tighten the ✦-to-label gap: the global button rule sets gap:6px, which
       (plus the old spark margin) left the star floating too far from the text. */
    gap: 3px;
  }
  .draft-actions .draft-ai-btn:hover {
    background: var(--bg-hover);
    border-color: transparent;
    color: var(--text);
  }
  /* Edit / Cancel — FILLED secondary (respond-chase-v1: `.today-mini-btn` has a
     `--panel-3` surface; only Dismiss is `.ghost`/transparent). Gives the action
     bar a real middle tier: Send (solid coral) › Edit/Cancel (filled) › ✦ tools
     & Dismiss (borderless/transparent). Excludes .ghost (Dismiss), .draft-ai-btn
     (✦ tools) and .draft-discuss-btn so only Edit/Cancel pick up the surface.
     Scoped to :not(.inline-right) so ONLY the expanded footer bar's Edit/Cancel
     fill — the collapsed head's inline-right quick-actions (Snooze 7d, chase
     verbs) stay transparent/ghost-tier as the mock wants. Specificity clears the
     base .today-mini-btn — no !important. `--bg-hover` is the nearest NEUTRAL raised
     surface the live tokens have (no exact `--panel-3`); deliberately not a coral
     tint — §2 keeps solid coral = commit. Hover is left to the base .today-mini-btn
     rule (brighter text + `--border-strong`), mirroring the mock, which keeps the
     fill and only shifts border/text — NOT `--bg-active`, which is coral-tinted in
     both themes and would muddy the neutral read. */
  .draft-actions:not(.inline-right) .today-mini-btn:not(.ghost):not(.draft-ai-btn):not(.draft-discuss-btn) {
    background: var(--bg-hover);
  }
  /* Characterful nav verbs (Set next step → / Try another angle → / Open calendar →
     / Top up the funnel →) — coral text-links, NOT boxed pills. They navigate
     (doDecisionPrimary → $currentView), and DECISIONS 2026-06 settled "nav verb =
     coral text link, no box." Same form as .draft-discuss-btn — DESIGN-SYSTEM §2
     groups Discuss + characterful verbs as one. Specificity (0,3,0 / 0,4,0) clears
     the .today-mini-btn box + its :hover — no !important. */
  .draft-actions .today-mini-btn.draft-navlink {
    background: transparent;
    border: 1px solid transparent;   /* reserve the border box → same height as siblings */
    color: var(--accent-ink);
    text-decoration: underline;
    text-underline-offset: 2px;
    padding-left: 4px;
    padding-right: 4px;
  }
  .draft-actions .today-mini-btn.draft-navlink:hover {
    color: var(--accent-hover);
    background: transparent;
  }
  /* One Dismiss, pinned to the right of the EXPANDED footer bar (respond-chase-v1
     "one Dismiss only … pinned bottom-right"). Scoped to :not(.inline-right) so
     the collapsed header's right-aligned action group is untouched. */
  .draft-actions:not(.inline-right) > .today-mini-btn.ghost { margin-left: auto; }
  /* ── Action-bar alignment: one shared box so Send / Edit / Cancel / ✦ tools /
     Discuss render at the SAME height + baseline: one font, one line-height, one
     vertical padding, with each button centring its own content. (Border boxes are
     equalised above: bordered buttons + a transparent 1px border on the ✦/
     nav/discuss variants.) Sits BEFORE the @media block so narrow-screen sizing
     still wins. ── */
  .draft-actions .btn-primary,
  .draft-actions .today-mini-btn {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    line-height: 1.4;
    padding-top: 5px;
    padding-bottom: 5px;
    box-sizing: border-box;
  }
  /* ✦ rework tools are text-first — snug horizontal padding so a long label
     ("Refine content") doesn't read far wider than the filled Send/Edit. */
  .draft-actions .draft-ai-btn { padding-left: 7px; padding-right: 7px; }
  @media (max-width: 560px) {
    .draft-actions .today-mini-btn,
    .draft-actions .btn-primary {
      font-size: 11px;
      padding: 4px 8px;
    }
  }
  @media (max-width: 420px) {
    .draft-actions .today-mini-btn,
    .draft-actions .btn-primary {
      font-size: 10.5px;
      padding: 3px 6px;
    }
    .draft-actions { gap: 4px; row-gap: 4px; }
  }

  /* ─── Critical task tile (v10 style) ─── */
  .ctask-swipe-wrap {
    position: relative;
    margin-bottom: 10px;
    overflow: hidden;
    border-radius: 12px;
  }
  .ctask {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    column-gap: 13px;
    row-gap: 0;
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 15px 18px;
    overflow: hidden;
    transition: border-color 0.12s, background 0.12s;
  }
  /* Collapsed = title + one preview line: vertically CENTER the serif number
     against that 2-line block (flex-start left it stuck to the top, reading
     as misaligned). Expanded stays top-aligned (base rule) so the number
     doesn't float to the middle of a tall open card. */
  .ctask:not(.open) { align-items: center; }
  .ctask:hover { border-color: var(--border-strong); }
  /* Same hover rule as .draft-card: bg light-up only while collapsed, so the two
     card families behave identically and nothing blends once expanded. */
  .ctask:not(.open):hover { background: var(--bg-hover); }
  /* Accent bar — severity for Critical, note-type for Briefing (via the
     --ctask-accent inline var); falls back to a neutral edge. */
  .ctask::before {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 3px;
    background: var(--ctask-accent, var(--border-strong));
  }
  .ctask.sev-red::before { background: var(--red); }
  .ctask.sev-amber::before { background: #F5B042; }
  .ctask.sev-green::before { background: var(--green); }
  .ctask-num {
    flex-shrink: 0;
    width: 22px;
    text-align: center;
    font-family: 'Fraunces', Georgia, serif;
    font-size: 22px;
    font-weight: 500;
    color: var(--text-dim);
  }
  .ctask-body { flex: 1; min-width: 0; }
  .ctask-name {
    display: flex;
    align-items: center;
    gap: 9px;
    font-size: 15px;
    font-weight: 600;
    color: var(--text);
    margin-bottom: 3px;
  }
  .ctask-pill {
    flex-shrink: 0;
    font-family: var(--font-mono);
    font-size: 9.5px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 2px 7px;
    border-radius: 5px;
    border: 1px solid color-mix(in srgb, currentColor 32%, transparent);
    background: color-mix(in srgb, currentColor 12%, transparent);
  }
  .ctask-why {
    font-size: 12.5px;
    line-height: 1.5;
    color: var(--text-dim);
    /* 2-line clamp for scan-ability. Variable heights are fine — short
       briefs get a tight tile, long ones get exactly 2 lines with a
       word-boundary "…" at the end of line 2. User clicks to expand
       for the full note. */
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
  }
  .ctask-why .hl { color: var(--text-muted); font-weight: 500; }
  /* Action zone (today-brief-v20 spec): collapsed → hidden (the verb shows on
     the right of the row instead); expanded → its own full-width footer below
     the body, verb bottom-LEFT + Dismiss bottom-RIGHT. Scoped to .ctask, which
     in markup is the Briefing card only. */
  .ctask-actions {
    display: none;
    flex-basis: calc(100% - 35px);
    margin-left: 35px;
    align-items: center;
    gap: 8px;
  }
  .ctask.open .ctask-actions { display: flex; }
  /* Dismiss only when expanded (§6: briefings are reads, not tasks), pinned right */
  .ctask:not(.open) .ctask-actions .today-mini-btn.ghost { display: none; }
  .ctask.open .ctask-actions .today-mini-btn.ghost { margin-left: auto; }
  /* Cards with no single verb (dry-funnel) keep their collapsed shortcut visible */
  .ctask:not(.has-verb):not(.open) .ctask-actions { display: flex; }
  /* Verb = coral text link (DESIGN-SYSTEM §6/§2), no box/fill. Coral not the
     per-type accent — a blue accent verb would violate §1 (blue never an action). */
  .ctask-btn {
    font-family: inherit;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--accent-ink);
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    white-space: nowrap;
  }
  .ctask-btn:hover { text-decoration: underline; text-underline-offset: 3px; }
  /* Collapsed verb — body-level, right side, vertically centred to the preview
     (below the time). Hidden when expanded (verb re-appears in the footer). */
  .ctask-verb-right { display: none; }
  .ctask.has-verb:not(.open) .ctask-verb-right {
    display: inline-flex;
    position: absolute;
    /* 18px = the card's own right padding, so the action verb aligns with the
       content edge instead of sitting 3px closer to the right than everything
       else (read as "too close to the right"). */
    right: 18px;
    top: calc(50% + 8px);
    transform: translateY(-50%);
  }
  /* Preview reserves right room so its text clamps before the verb, never under it */
  .ctask.has-verb .ctask-why { padding-right: 150px; }
  /* Chevron BESIDE the title (mirrors Respond's .draft-chev): ⌄ collapsed (points
     DOWN = "expand ↓", same direction as "Show N more ↓"), ^ open ("collapse ↑").
     Size + rotation match Respond so the two card families read the same. No
     margin-left:auto — it sits next to the tag, not pinned far-right. */
  .ctask-chev {
    display: inline-block;
    color: var(--text-dim);
    font-size: 15px;
    line-height: 1;
    flex-shrink: 0;
    transition: transform 0.2s;
    transform: rotate(90deg);
  }
  .ctask.open .ctask-chev { transform: rotate(270deg); }
  .ctask-sub {
    font-family: inherit;
    font-size: 12px;
    color: var(--text-dim);
    background: transparent;
    border: none;
    padding: 4px 2px;
    cursor: pointer;
    white-space: nowrap;
  }
  .ctask-sub:hover { color: var(--text-muted); text-decoration: underline; }

  /* Hover feedback is border/background only. A prior `translateX(3px)` "slide on
     hover" shifted these FULL-WIDTH tiles right, and the 3px overflowed
     .main-content's `overflow-x: clip` → the right edge read as "cut off" on hover.
     Removed the shift (no room for it in a clip container); the inner card/tile
     border + background still signal hover. */
  .gnote-tile { transition: border-color 0.12s, background 0.12s; }

  /* ─── Re-engage tile — dense one-line variant (the cold list grows) ─── */
  .rtask { padding: 11px 18px; gap: 14px; }
  .rtask .ctask-num { font-size: 17px; }
  .rtask-body {
    flex-grow: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 9px;
  }
  .rtask-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .rtask-meta {
    flex-shrink: 0;
    font-size: 11.5px;
    color: var(--text-dim);
    font-family: var(--font-mono);
    white-space: nowrap;
  }

  /* ─── Prospect weekly progress ─── */
  /* ─── Call card ─── */
  .call-card {
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-left: 3px solid var(--blue);
    border-radius: 8px;
    padding: 11px 14px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .call-time {
    font-family: var(--font-mono);
    font-size: 13px;
    color: var(--blue);
    font-weight: 500;
    min-width: 72px;
  }
  .call-body { flex-grow: 1; min-width: 0; }
  .call-title { font-size: 13px; font-weight: 500; color: var(--text); }
  .call-meta { font-size: 11.5px; color: var(--text-dim); }
  /* Secondary "download brief" ghost — inline-flex so the Feather download
     icon and its "Brief" label sit on one baseline. */
  .call-dl-btn { display: inline-flex; align-items: center; gap: 5px; }
  .call-dl-icon { width: 13px; height: 13px; flex-shrink: 0; }
  /* Primary "Prepare more →" action — opens the matched client's page. Matches
     the EXACT coral verb treatment the page already uses (.ctask-btn
     "Re-engage →" / "Plan a recovery →"): coral, weight 600, no box, underline
     on HOVER ONLY. padding:0 → the short verb sits flex-centered against the
     taller sibling pills, same as every other .ctask-btn verb. */
  .call-brief-link {
    font-family: inherit;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--accent-ink);
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    white-space: nowrap;
  }
  .call-brief-link:hover { text-decoration: underline; text-underline-offset: 3px; }

  /* ─── Sends table ─── */
  .sends-table {
    width: 100%;
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    border-collapse: collapse;
    overflow: hidden;
  }
  .sends-table td {
    height: var(--row-h);
    /* 5px, not 9: `.today-mini-btn` in the actions cell is ~29px tall, and 9px either side
       pushed the row to ~47px — past `--row-h`, so the shared height never applied. */
    padding: 5px 12px;
    border-bottom: 1px solid var(--border);
    font-size: 12.5px;
    color: var(--text);
  }
  .sends-table tr:last-child td { border-bottom: none; }
  .sends-time {
    font-family: var(--font-mono);
    font-size: 11.5px;
    color: var(--text-muted);
    width: 80px;
    white-space: nowrap;
  }
  .sends-to { width: 240px; color: var(--text-muted); font-family: var(--font-mono); font-size: 11.5px; }
  .sends-subject { color: var(--text); }
  .sends-actions { width: 100px; text-align: right; }

  /* Clickable section header — whole header strip navigates to Scheduled.
     Subtle hover cue + a small "View all →" link on the right so the
     affordance is discoverable without screaming. Matches the section-
     header rhythm used elsewhere in Today. */
  .sends-header {
    cursor: pointer;
    transition: background 0.12s;
    border-radius: 6px;
    margin: -4px -8px 0;
    padding: 4px 8px;
  }
  .sends-header:hover {
    background: color-mix(in srgb, var(--accent) 6%, transparent);
  }

  /* Each row also clickable → Scheduled (focused on its own send).
     Cancel button stops propagation so clicking Cancel doesn't navigate. */
  .sends-row {
    cursor: pointer;
    transition: background 0.1s;
  }
  .sends-row:hover {
    background: color-mix(in srgb, var(--accent) 4%, transparent);
  }
  .sends-row:hover .sends-subject {
    color: var(--accent-ink);
  }


  .stage-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  /* ─── Empty state ─── */
  .today-empty {
    font-size: 12.5px;
    color: var(--text-dim);
    font-style: italic;
    padding: 14px 16px;
    background: var(--bg-surface);
    border: 1px dashed var(--border);
    border-radius: 8px;
  }

  /* ─── Prospect / hunt ─── */

  .hunt-grid {
    display: grid;
    /* auto-fit (not auto-fill) so the cards stretch to consume the full
       width — no dead space on the right when there are only 3-4 cards. */
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 10px;
    margin-bottom: 14px;
  }
  .hunt-card {
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-top: 2px solid var(--border-strong);
    border-radius: 8px;
    padding: 12px 13px;
    display: flex;
    flex-direction: column;
    gap: 5px;
    transition: border-color 0.1s, transform 0.08s;
  }
  .hunt-card:hover { border-color: var(--border-strong); transform: translateY(-1px); }
  .hunt-card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .hunt-slot-tag {
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--text-dim);
  }
  .hunt-card-name {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--text);
    letter-spacing: -0.01em;
  }
  .hunt-card-sub {
    font-size: 11px;
    color: var(--text-dim);
  }
  .hunt-card-btn {
    margin-top: 6px;
    background: transparent;
    border: 1px solid var(--accent);
    color: var(--accent-ink);
    padding: 6px 10px;
    border-radius: 5px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.1s;
  }
  .hunt-card-btn:hover { background: var(--accent); color: #fff; }

  .hunt-scoreboard {
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 12px 14px;
    margin-top: 6px;
  }
  /* In Prospect-section's empty-state path, pull the streak banner and
     scoreboard chart tighter against the empty-state pill so the three
     blocks read as one section rather than three orphan cards. */
  #today-prospect .today-empty + .hunt-scoreboard { margin-top: 6px; }
  .hunt-scoreboard-head {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 8px;
  }
  .hunt-scoreboard-title {
    font-size: 12px;
    font-weight: 600;
    color: var(--text);
  }
  .hunt-scoreboard-sub {
    font-size: 11px;
    color: var(--text-dim);
    font-style: italic;
  }
  .hunt-chart-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 8px;
  }
  .hunt-legend-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    color: var(--text-muted);
  }
  .hunt-legend-dot {
    width: 8px; height: 8px;
    border-radius: 2px;
  }
  .hunt-legend-total {
    font-family: var(--font-mono);
    color: var(--text);
    font-weight: 600;
  }

  /* ─── Mobile ─── */
  @media (max-width: 720px) {
    .sends-to { display: none; }
    .draft-time { display: none; }
    .hunt-grid { grid-template-columns: 1fr 1fr; }
    .george-transition span { white-space: normal; }
    .ctask { flex-wrap: wrap; }
    .ctask-actions { width: 100%; padding-left: 38px; }

    /* ─── New draft-card tile family (Respond + Critical + Re-engage) on
       mobile. The desktop layout tries to fit number + 3-line body +
       inline-right action buttons in one row; that wraps awkwardly on
       narrow screens. Mobile drops the actions to a second row pinned
       under the body, full width, aligned under the body content. */
    .draft-card {
      padding: 12px 14px;
      gap: 8px;
    }
    .draft-card .draft-head {
      flex-wrap: wrap;
      row-gap: 8px;
    }
    .draft-card .draft-head-body {
      width: 100%;
      min-width: 0;
      flex-basis: calc(100% - 38px);
    }
    .draft-card .draft-head-row {
      flex-wrap: wrap;
    }
    .draft-actions.inline-right {
      width: 100%;
      margin-left: 0;
      padding-left: 38px;          /* aligns under .draft-head-body */
      justify-content: flex-start;
      gap: 6px;
    }
    .draft-actions.inline-right .today-mini-btn {
      font-size: 11px;
      padding: 4px 8px;
    }
    /* The number prefix should stay small on mobile so it doesn't dominate
       the row. */
    .draft-num { font-size: 18px; width: 20px; }
    /* Long subjects often run off — let them wrap into 2 lines on mobile
       rather than ellipsing under tight space. */
    .draft-subtitle, .draft-subject-line {
      white-space: normal;
    }
    /* Body preview readability — slightly tighter line-height + don't let
       the green-accent box squeeze the body text on narrow viewports. */
    .draft-preview {
      font-size: 12.5px;
      line-height: 1.45;
      padding: 10px 12px;
    }
    .draft-edit input {
      font-size: 13px;
    }
  }
</style>
